import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import type { V2GetPortfolioQueueResponse } from '@workspace/api-backend'
import { v2EnablePortfolioOutreach } from '@workspace/api-backend'
import {
  v2GetPortfolioOutreachStatusOptions,
  v2GetPortfolioQueueOptions,
  v2GetStartupOutreachOverviewOptions,
  v2PausePortfolioOutreachMutation,
  v2ResumePortfolioOutreachMutation,
} from '@workspace/api-backend/react-query'
import { Button } from '@workspace/ui/components/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@workspace/ui/components/dialog'
import { CtaButton } from '@workspace/ui/components/dsm/cta-button'
import {
  SecondaryButton,
  secondaryLinkClasses,
} from '@workspace/ui/components/dsm/secondary-button'
import { StatCell } from '@workspace/ui/components/stat-cell'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@workspace/ui/components/tooltip'
import { cn } from '@workspace/ui/lib/utils'
import {
  Archive,
  Check,
  ExternalLink,
  Info,
  Linkedin,
  Loader2,
  Pause,
  Pencil,
  Play,
  SkipForward,
  Sparkles,
  Star,
  X,
} from 'lucide-react'
import {
  Fragment,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
  type RefCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useStartupContext } from '../../../contexts/startup-guard'
import { useStartupMatchesQuery } from '../../../hooks/api'
import { trackEvent } from '../../../lib/gtag'
import { deriveContactsToReach } from '../../../lib/matches'
import { useStartupFeatureFlagsQuery } from '../../feature-flags/hooks/use-startup-feature-flags'
import { useInvalidateHomeState } from '../../home/hooks/use-home-state'
import { formatTicket, GEO_LABELS, SECTOR_LABELS, STAGE_LABELS } from '../../matches/config/labels'
import { outreachApi } from '../api'
import { LaunchFlowProvider, useLaunchFlow } from '../context/launch-flow-context'
import { useDisableOutreachMutation } from '../hooks/use-disable-outreach-mutation'
import { useEnableOutreachMutation } from '../hooks/use-enable-outreach-mutation'
import { useIncrementalReveal } from '../hooks/use-incremental-reveal'
import {
  useArchiveContactMutation,
  useArchivePortfolioMessageMutation,
  useRemoveInProgressLeadMutation,
  useSkipAllPortfolioQueuedMutation,
} from '../hooks/use-outreach-archive'
import { useOutreachContactsToReach } from '../hooks/use-outreach-hero-stats'
import { useOutreachMessagesQuery } from '../hooks/use-outreach-messages'
import { useOutreachQueueQuery } from '../hooks/use-outreach-queue'
import {
  useApproveAllQueuedMutation,
  useResolveQueuedMutation,
  useSkipAllQueuedMutation,
} from '../hooks/use-resolve-queued-mutation'
import type { OutreachMessageVariant, OutreachQueueItem, OutreachStatusData } from '../types'
import { OUTREACH_UNLOCK_STATUSES } from '../types'
import {
  decorateLegacyOutreachPreview,
  fundTokensFromProfile,
  isLegacyOutreachField,
  type OutreachTrack,
} from '../utils/render-template-preview'
import { CHANNEL_LABELS, type ChannelId } from './channel-switcher'
import { isLinkedInConnected } from './connect-linkedin-action'
import { DisconnectLinkedInButton } from './disconnect-linkedin-button'
import { OutreachConversationSequence } from './outreach-conversation-preview'
import { OutreachLaunchBar } from './outreach-launch-bar'
import { OutreachLaunchPanel } from './outreach-launch-panel'
import { OutreachPreLaunchBoard } from './outreach-prelaunch-board'
import { OutreachTopUpDialog } from './outreach-topup-dialog'
import { PortfolioPreLaunchBoard } from './portfolio-prelaunch-board'

interface Props {
  startupId: string
  outreachStatus: OutreachStatusData | null
  /** Campaign channel on screen. Owned by the route so the sidebar can deep-link to it. */
  channel: ChannelId
}

// Statuses that only exist once a campaign has been launched (built / running /
// paused). Legacy rows launched before founderApprovedAt was introduced carry a
// null stamp — their status is the source of truth, so they keep the live
// dashboard (pause/resume, funnel, roster) instead of being dropped back into
// the setup wizard.
const LAUNCHED_STATUSES = new Set<OutreachStatusData['status']>([
  'READY',
  'PAUSED',
  'PAUSED_DUE_TO_BUDGET',
  'ERROR',
])

export function OutreachActiveView({ startupId, outreachStatus, channel: activeTab }: Props) {
  const { investorMatchesUnlocked } = useStartupContext()
  const status = outreachStatus?.status ?? null
  // Launched = the founder has approved & launched the campaign (or the row is
  // in a campaign-running status — see LAUNCHED_STATUSES). Pre-launch the
  // dashboard shows the setup checklist + matches preview board instead of the
  // live campaign UI (funnel / roster / pause controls).
  const isLaunched = outreachStatus?.founderApprovedAt != null || LAUNCHED_STATUSES.has(status)
  const isUnlocked =
    investorMatchesUnlocked || (status != null && OUTREACH_UNLOCK_STATUSES.has(status))
  const overviewQuery = useQuery(v2GetStartupOutreachOverviewOptions({ path: { id: startupId } }))
  const matchesQuery = useStartupMatchesQuery(startupId, 50)
  const queueQuery = useOutreachQueueQuery(startupId)

  const overview = overviewQuery.data?.status === 'ok' ? overviewQuery.data.data : null

  const visibleMatches = (matchesQuery.data?.data?.matches ?? []).filter(
    (m) => isUnlocked || m.source !== 'paid_eval'
  )
  // `matches` is a paginated page (capped by the query limit), so its length
  // under-reports once there are more matches than the page size. The backend
  // returns `total` = the uncapped count of matched investors — use that for
  // the headline stat so it isn't throttled by the fetch limit.
  const lockedSummary = matchesQuery.data?.data?.lockedSummary ?? null
  const totalMatches =
    lockedSummary?.totalRemaining ?? matchesQuery.data?.data?.total ?? visibleMatches.length
  // Outreach targets every matched fund regardless of unlock state, so count
  // contacts across the full match set. Until activity research has run for
  // every matched fund we show a per-fund estimate keyed off the displayed
  // match count, so the stat doesn't read as a misleadingly low exact count.
  const { count: totalContacts, estimated: investorContactsEstimated } = deriveContactsToReach(
    totalMatches,
    matchesQuery.data?.data?.contactReach,
    visibleMatches.reduce((sum, m) => sum + m.investorContacts.length, 0)
  )

  // User pause is the single investor channel flag now (status stays READY), so
  // surface 'paused' from the flag. Budget pause keeps its own status, which
  // deriveVariant maps to 'paused-budget'.
  const variant: ActiveVariant = !isLaunched
    ? 'setup'
    : outreachStatus?.directInvestorOutreachEnabled === false && status !== 'PAUSED_DUE_TO_BUDGET'
      ? 'paused'
      : deriveVariant(status)

  const portfolioStatusQuery = useQuery({
    ...v2GetPortfolioOutreachStatusOptions({ path: { id: startupId } }),
    // While portfolio research is still discovering founders, poll so the Queued
    // estimate ("~N") and "preparing more" footer update live instead of waiting
    // for a window refocus. Stops as soon as research finishes.
    refetchInterval: (query) =>
      query.state.data?.status === 'ok' && query.state.data.data.researchInProgress ? 3000 : false,
  })
  const portfolioData =
    portfolioStatusQuery.data?.status === 'ok' ? portfolioStatusQuery.data.data : null
  const portfolioEnabled = portfolioData?.enabled ?? false
  const featureFlagsQuery = useStartupFeatureFlagsQuery(startupId)
  const portfolioFlagOn = featureFlagsQuery.data?.portfolioOutreach ?? false

  // Both channels run by default now ("Choose campaigns" was removed). During
  // pre-launch setup, auto-enable portfolio once when the flag is on but it's
  // never been turned on — this generates its messages and kicks research so the
  // portfolio tab/contacts/review surface without an explicit opt-in. The Skip
  // button in the review modal is how a founder opts back out.
  const autoEnabledRef = useRef(false)
  const queryClient = useQueryClient()
  useEffect(() => {
    if (autoEnabledRef.current) return
    if (isLaunched || !portfolioFlagOn) return
    // Only act once status has loaded and reports the channel as off.
    if (portfolioStatusQuery.data?.status !== 'ok' || portfolioEnabled) return
    autoEnabledRef.current = true
    void v2EnablePortfolioOutreach({ path: { id: startupId } })
      .then(() =>
        queryClient.invalidateQueries({
          queryKey: v2GetPortfolioOutreachStatusOptions({ path: { id: startupId } }).queryKey,
        })
      )
      .catch(() => {
        autoEnabledRef.current = false
      })
  }, [
    isLaunched,
    portfolioFlagOn,
    portfolioEnabled,
    portfolioStatusQuery.data?.status,
    startupId,
    queryClient,
  ])
  // Whether portfolio outreach has been set up for this startup (campaign built
  // or founders found). Drives card VISIBILITY independently of the running flag
  // (`enabled`), so a *paused* portfolio still shows its card with a Resume
  // button rather than collapsing back to the "Activate" CTA.
  const portfolioConfigured =
    portfolioFlagOn &&
    (portfolioEnabled ||
      portfolioData?.campaignId != null ||
      (portfolioData?.contactsCount ?? 0) > 0)
  const portfolioQueueQuery = useQuery(v2GetPortfolioQueueOptions({ path: { id: startupId } }))
  const portfolioQueue =
    portfolioQueueQuery.data?.status === 'ok' ? portfolioQueueQuery.data.data : null

  // Portfolio is running: the flag is on for this startup AND the channel itself
  // is enabled. Drives the hero's live/paused read for the warm-intro campaign.
  const portfolioOn = portfolioEnabled && portfolioFlagOn
  // Hero "contacts to reach" — single source of truth shared with the dashboard
  // and the wallet-side stat (launched ⇒ exact queued count, pre-launch ⇒
  // investor estimate + portfolio founders), so the three never drift.
  const contactsToReach = useOutreachContactsToReach(startupId)
  const heroContactsCount = contactsToReach.count
  const heroContactsEstimated = contactsToReach.estimated
  // The hero speaks for the ONE campaign on screen, so "live" vs "paused" tracks
  // that campaign's own flag — not "either campaign is running". Everything else
  // (setup, building, reconnect, budget pause, error) is shared by both channels
  // and comes straight from the row status.
  const activeChannelEnabled =
    activeTab === 'portfolio'
      ? portfolioOn
      : (outreachStatus?.directInvestorOutreachEnabled ?? true)
  const statusVariant: ActiveVariant = isLaunched ? deriveVariant(status) : 'setup'
  // Has anyone on the campaign on screen actually been approved? A lead leaves
  // `awaiting` only by the founder approving it.
  //
  // For portfolio that is NOT the same as "sits outside the queued bucket". The
  // portfolio board buckets by `pushedAt`, not by approval, so a contact the
  // founder has approved stays in `queued` until a push actually goes out —
  // carrying approvalState 'approved'. Reading only the non-queued buckets said
  // nobody was approved whenever sending had not started yet, which put a
  // roster of approved contacts under "Approve who we reach". Worse, the two
  // compound: while pushes are failing nothing ever leaves `queued`, so the
  // page asks for an approval that has already been given, indefinitely.
  const channelHasApproved =
    activeTab === 'portfolio'
      ? (portfolioQueue?.queued ?? []).some((item) => item.approvalState === 'approved') ||
        (portfolioQueue?.active.length ?? 0) +
          (portfolioQueue?.warmIntro.length ?? 0) +
          (portfolioQueue?.finished.length ?? 0) >
          0
      : (queueQuery.data ?? []).some((item) => item.status !== 'awaiting')
  const heroVariant: ActiveVariant =
    statusVariant !== 'ready'
      ? statusVariant
      : !activeChannelEnabled
        ? 'paused'
        : // READY means our side is built and sending is switched on. It does not
          // mean anything is going out: every lead starts at `awaiting` and only
          // the founder can move it. Reporting a green "running" campaign that
          // has nobody to send to is the page contradicting its own roster.
          channelHasApproved
          ? 'ready'
          : 'awaiting-contacts'
  // Before the campaign goes live the row sits at AWAITING_CAMPAIGN (still being
  // assembled) — the live roster has no leads yet, so keep showing the
  // pre-launch preview boards (the approved contacts, both channels) instead of
  // an empty board. AWAITING_SENDER counts too: the campaign was cleared by a
  // workspace rebuild, so the "live" roster/funnel would be pointing at a dead
  // campaign — fall back to the same pre-launch boards until it's rebuilt.
  const building = isLaunched && (variant === 'awaiting-campaign' || variant === 'awaiting-sender')
  // Once the investor leads are materialized (the queue has rows), the live
  // InvestorRoster is the source of truth for approval — approving there flips
  // the lead status (AWAITING_APPROVAL → QUEUED), which the preview board's
  // contact-decisions do NOT do for already-created leads. So even while
  // `building` (awaiting admin approval), prefer the roster the moment leads
  // exist; the preview board only stands in before any leads are materialized.
  const investorLeadsMaterialized = (queueQuery.data?.length ?? 0) > 0
  // The portfolio channel is its own campaign and can already be live and sending
  // while the investor row is still in the building/awaiting window. So the
  // portfolio board tracks the portfolio campaign's own state (campaignId), not
  // the investor-driven `building` flag — only show its preview board while the
  // portfolio campaign itself hasn't been built yet. Once it exists, the live
  // PortfolioRoster (with its In progress / Finished columns) takes over.
  const portfolioPreLaunch = (!isLaunched || building) && portfolioData?.campaignId == null

  // Whether the channel on screen is showing its live roster, which brings its
  // own section header. Drives whether the shared pre-launch caption is wanted.
  const rosterVisible =
    isLaunched &&
    (activeTab === 'investors'
      ? !building || investorLeadsMaterialized
      : !portfolioPreLaunch && portfolioData != null)

  // Every matched fund renders immediately, in fit order, built as full roster
  // entries (with fund profile) so the cards open the same approval modal as the
  // live board. Funds whose activity research has populated real GP contacts show
  // one card per contact; funds still being researched show a single fund-level
  // placeholder ("partner being prepared"), so the founder sees the full roster
  // up front instead of just the handful of funds that happen to be researched.
  // Empty until matches load. The board shows 2 while locked, all once the
  // profile step is done.
  const labelOf = (map: Record<string, string>, value: string) => map[value] ?? value
  const profileOf = (m: (typeof visibleMatches)[number]): QueuedProfile => ({
    fitScore: m.rerankScore ?? null,
    description: m.investorDescription ?? '',
    stages: m.investorStages.map((s) => labelOf(STAGE_LABELS, s)),
    sectors: m.investorSectors.map((s) => labelOf(SECTOR_LABELS, s)),
    geographies: m.investorGeographies.map((g) => labelOf(GEO_LABELS, g)),
    ticket: formatTicket(m.investorMinTicket, m.investorMaxTicket) ?? '',
    reasoning: m.reasoning ?? '',
    portfolio: (m.investorRecentPortfolios ?? []).map((p) => p.name),
  })
  const previewEntries: RosterMatch[] = []
  for (const m of visibleMatches) {
    if (m.investorContacts.length > 0) {
      for (const c of m.investorContacts) {
        previewEntries.push({
          investorId: m.investorId,
          leadId: `preview-${m.investorId}-${c.id}`,
          contactId: c.id,
          investorName: m.investorName,
          contactName: c.name,
          contactTitle: c.title,
          contactScope: c.scope,
          group: 'queued',
          stage: 'Awaiting approval',
          charges: { views: 0, returnVisits: 0, calls: 0 },
          linkedinUrl: c.linkedinUrl ?? undefined,
          profile: profileOf(m),
        })
      }
    } else {
      previewEntries.push({
        investorId: m.investorId,
        leadId: `preview-fund-${m.investorId}`,
        investorName: m.investorName,
        contactName: null,
        contactTitle: null,
        contactScope: null,
        contactPending: true,
        researchInProgress: m.investorContactsResearchInProgress,
        group: 'queued',
        stage: 'Awaiting approval',
        charges: { views: 0, returnVisits: 0, calls: 0 },
        profile: profileOf(m),
      })
    }
  }

  return (
    <LaunchFlowProvider
      startupId={startupId}
      outreachStatus={outreachStatus}
      contactsCount={heroContactsCount}
      contactsEstimated={heroContactsEstimated}
      investorContactsCount={totalContacts}
      investorContactsEstimated={investorContactsEstimated}
      previewContacts={previewEntries}
    >
      <div className="mx-auto max-w-6xl pb-6">
        {/* Value-first: lead with the matches/value hero, then the setup ask,
            then the board, then the launch closer. Founders see proof before
            the commitment. */}
        <ActiveHero
          startupId={startupId}
          variant={heroVariant}
          channel={activeTab}
          isLaunched={isLaunched}
          linkedinConnected={isLinkedInConnected(outreachStatus)}
          campaignLabel={isLaunched ? CHANNEL_LABELS[activeTab] : undefined}
          actions={
            // Investors always exist; the warm-intro controls only make sense once
            // that campaign is configured. Pre-launch these are the on/off toggles,
            // which need both channels to be meaningful.
            (
              activeTab === 'portfolio'
                ? portfolioConfigured
                : isLaunched || portfolioConfigured
            ) ? (
              <CampaignActions
                channel={activeTab}
                startupId={startupId}
                isLaunched={isLaunched}
                investorEnabled={outreachStatus?.directInvestorOutreachEnabled ?? true}
                portfolioEnabled={portfolioEnabled}
              />
            ) : undefined
          }
        />
        {!isLaunched && <OutreachLaunchPanel />}
        {/* awaiting-campaign renders no panel: it restated the hero's headline
            word for word and repeated the hero's own Pause / Edit messages /
            Disconnect controls a few hundred pixels lower. Nothing on it was
            actionable, and the roster below is the real next step. Same reason
            the admin-approval phase never had one. */}
        {/* awaiting-sender is a launched campaign whose HeyReach sender was lost
            (workspace deleted + auto-rebuilt) — there IS a founder action here
            (reconnect LinkedIn), so render the normal actionable checklist
            panel instead of the passive "being prepared" waiting copy above. */}
        {isLaunched && variant === 'awaiting-sender' && <OutreachLaunchPanel />}
        {/* Contacts caption sits above the channel tab bar so it heads the whole
            pre-launch board group (both channels), not each board individually.
            It heads the PREVIEW boards only: once leads are materialized the
            live roster renders its own "Campaign roster" header, and both
            appearing left one list under two competing titles. */}
        {(!isLaunched || building) && !rosterVisible && (
          <div className="mb-4">
            <h2 className="text-[18px] font-bold tracking-[-0.01em] text-foreground">
              Who we'll contact
            </h2>
            <p className="mt-0.5 text-[13px] text-muted-foreground">
              Tap a card to see the full profile and message. Approve or decline each individual
              contact.
            </p>
          </div>
        )}
        {isLaunched &&
          !portfolioConfigured &&
          portfolioFlagOn &&
          (variant === 'ready' || variant === 'paused') && (
            <PortfolioFeatureToggleCard startupId={startupId} />
          )}
        {isLaunched && !building && activeTab === 'investors' && overview && (
          <FunnelGrid kpi={overview.kpi} funnel={overview.funnel} />
        )}
        {isLaunched && !building && activeTab === 'portfolio' && (
          <PortfolioFunnelGrid queue={portfolioQueue} />
        )}
        {(!isLaunched || building) &&
          !investorLeadsMaterialized &&
          (!portfolioConfigured || activeTab === 'investors') && (
            <OutreachPreLaunchBoard
              entries={previewEntries}
              totalContacts={totalContacts}
              contactsEstimated={investorContactsEstimated}
            />
          )}
        {portfolioPreLaunch &&
          portfolioConfigured &&
          activeTab === 'portfolio' &&
          portfolioData && (
            <PortfolioPreLaunchBoard
              startupId={startupId}
              portfolioStatus={portfolioData}
              contactsEstimated={heroContactsEstimated}
            />
          )}
        {isLaunched && (!building || investorLeadsMaterialized) && activeTab === 'investors' && (
          <InvestorRoster
            startupId={startupId}
            variant={variant}
            contactsEstimated={heroContactsEstimated}
          />
        )}
        {isLaunched && !portfolioPreLaunch && activeTab === 'portfolio' && portfolioData && (
          <PortfolioRoster
            startupId={startupId}
            portfolioStatus={portfolioData}
            contactsEstimated={heroContactsEstimated}
          />
        )}
        {!isLaunched && <OutreachLaunchBar />}
      </div>
    </LaunchFlowProvider>
  )
}

// ════════════════════════════════════════════════════════════════════════════
// State
// ════════════════════════════════════════════════════════════════════════════

type ActiveVariant =
  | 'setup'
  | 'awaiting-sender'
  | 'awaiting-campaign'
  /** Built and switched on, but nobody approved yet, so nothing can send. */
  | 'awaiting-contacts'
  | 'ready'
  | 'paused'
  | 'paused-budget'
  | 'error'

function deriveVariant(status: OutreachStatusData['status']): ActiveVariant {
  switch (status) {
    case 'READY':
      return 'ready'
    case 'PAUSED':
      return 'paused'
    case 'PAUSED_DUE_TO_BUDGET':
      return 'paused-budget'
    case 'ERROR':
      return 'error'
    // A launched campaign can fall back here if its HeyReach workspace was
    // deleted and auto-rebuilt (see backend #rebuildWorkspaceIfDeleted) —
    // distinct from AWAITING_CAMPAIGN: there IS a founder action required
    // (reconnect LinkedIn), so it must not collapse into the passive "being
    // prepared" waiting copy below.
    case 'AWAITING_SENDER':
      return 'awaiting-sender'
    default:
      return 'awaiting-campaign'
  }
}

// ════════════════════════════════════════════════════════════════════════════
// Campaign controls — pause/resume + edit messages for the campaign on screen
// ════════════════════════════════════════════════════════════════════════════

function CampaignActions({
  channel,
  startupId,
  isLaunched,
  investorEnabled,
  portfolioEnabled,
}: {
  channel: ChannelId
  startupId: string
  /**
   * Post-launch the buttons are runtime controls (Pause/Resume → mutations).
   * Pre-launch there's no outreach row yet, so the buttons toggle the in-memory
   * channel selection (Turn on/off) held in the launch-flow context.
   */
  isLaunched: boolean
  /** Single per-channel flag: true ⇒ running (show Pause), false ⇒ paused (show Resume). */
  investorEnabled: boolean
  portfolioEnabled: boolean
}) {
  const enable = useEnableOutreachMutation(startupId)
  const disable = useDisableOutreachMutation(startupId)
  const queryClient = useQueryClient()
  // Home resolves its state from both campaigns, so pausing or resuming either
  // one has to reach it — otherwise Home keeps describing the campaign the
  // founder just changed.
  const invalidateHome = useInvalidateHomeState(startupId)
  const invalidatePortfolioStatus = () =>
    Promise.all([
      queryClient.invalidateQueries({
        queryKey: v2GetPortfolioOutreachStatusOptions({ path: { id: startupId } }).queryKey,
      }),
      invalidateHome(),
    ])
  const pausePortfolio = useMutation({
    ...v2PausePortfolioOutreachMutation(),
    onSuccess: invalidatePortfolioStatus,
  })
  const resumePortfolio = useMutation({
    ...v2ResumePortfolioOutreachMutation(),
    onSuccess: invalidatePortfolioStatus,
  })
  const { editMessages, channelEnabled, toggleChannel } = useLaunchFlow()
  const pending = enable.isPending || disable.isPending

  // Pre-launch on/off toggle: in-memory selection (no mutation — the outreach
  // row doesn't exist yet). We must contact at least one group, so the last
  // enabled group locks its "Skip" button.
  const preLaunchToggle = (kind: 'investors' | 'portfolio') => {
    const enabled = channelEnabled(kind)
    const lockedOn = enabled && !channelEnabled(kind === 'investors' ? 'portfolio' : 'investors')
    if (enabled) {
      return (
        <SecondaryButton
          size="sm"
          disabled={lockedOn}
          onClick={() => toggleChannel(kind)}
          icon={<Pause />}
        >
          Pause
        </SecondaryButton>
      )
    }
    return (
      <SecondaryButton
        size="sm"
        tone="success"
        disabled={lockedOn}
        onClick={() => toggleChannel(kind)}
        icon={<Play />}
      >
        Resume
      </SecondaryButton>
    )
  }

  const renderActions = (id: 'investors' | 'portfolio') =>
    id === 'investors' ? (
      <>
        {!isLaunched ? (
          preLaunchToggle('investors')
        ) : investorEnabled ? (
          <SecondaryButton
            size="sm"
            disabled={pending}
            onClick={() =>
              disable.mutate(undefined, {
                onSuccess: () => trackEvent('outreach_campaign_paused', { startup_id: startupId }),
              })
            }
            icon={<Pause />}
          >
            Pause
          </SecondaryButton>
        ) : (
          <SecondaryButton
            size="sm"
            tone="success"
            disabled={pending}
            onClick={() =>
              enable.mutate(undefined, {
                onSuccess: () => trackEvent('outreach_campaign_resumed', { startup_id: startupId }),
              })
            }
            icon={<Play />}
          >
            Resume
          </SecondaryButton>
        )}
        <SecondaryButton
          size="sm"
          onClick={() => {
            trackEvent('outreach_messages_edit_opened', { startup_id: startupId })
            editMessages()
          }}
          icon={<Pencil />}
        >
          Edit messages
        </SecondaryButton>
      </>
    ) : (
      <>
        {!isLaunched ? (
          preLaunchToggle('portfolio')
        ) : portfolioEnabled ? (
          <SecondaryButton
            size="sm"
            disabled={pausePortfolio.isPending}
            onClick={() => pausePortfolio.mutate({ path: { id: startupId } })}
            icon={<Pause />}
          >
            Pause
          </SecondaryButton>
        ) : (
          <SecondaryButton
            size="sm"
            tone="success"
            disabled={resumePortfolio.isPending}
            onClick={() => resumePortfolio.mutate({ path: { id: startupId } })}
            icon={<Play />}
          >
            Resume
          </SecondaryButton>
        )}
        <SecondaryButton size="sm" onClick={() => editMessages('portfolio')} icon={<Pencil />}>
          Edit messages
        </SecondaryButton>
      </>
    )

  return <div className="flex flex-wrap items-center gap-2">{renderActions(channel)}</div>
}

function PortfolioRoster({
  startupId,
  portfolioStatus,
  contactsEstimated = false,
}: {
  startupId: string
  portfolioStatus: {
    contactsCount: number
    campaignId: number | null
    messagesReviewedAt: string | null
    researchInProgress: boolean
  }
  contactsEstimated?: boolean
}) {
  const queryClient = useQueryClient()
  const queueQuery = useQuery(v2GetPortfolioQueueOptions({ path: { id: startupId } }))
  const data = queueQuery.data?.status === 'ok' ? queueQuery.data.data : null
  // Lifecycle: researching (no campaign yet) → awaiting admin approval (campaign
  // created, not yet verified) → live (admin approved). Founders can be approved
  // or declined as soon as research is done; nothing actually sends until the
  // campaign is live (the server pushes only while the outreach is READY and the
  // portfolio channel is on), so early approvals just pre-stage the send list.
  const isResearching = portfolioStatus.campaignId === null
  // Optimistic per-card approve/decline state, keyed by message id (leadId).
  const [resolutions, setResolutions] = useState<Record<string, ResolveState>>({})
  // Founder whose detail dialog is open (same dialog the investor board uses).
  const [detail, setDetail] = useState<RosterMatch | null>(null)

  const queued = (data?.queued ?? []).map((i) => portfolioItemToRoster(i, 'queued'))
  const active = (data?.active ?? []).map((i) => portfolioItemToRoster(i, 'in_progress'))
  const finished = (data?.finished ?? []).map((i) => portfolioItemToRoster(i, 'finished'))

  // Home too, not just the board. The sidebar's dot and its "N days left" are
  // Home's numbers now, and these approvals go through `outreachApi` directly
  // rather than useMutation — so the app-wide refresh-Home-after-any-mutation
  // hook never sees them, and the menu sat still while the founder worked
  // through the queue.
  const invalidateHome = useInvalidateHomeState(startupId)
  const invalidateQueue = () => {
    void Promise.all([
      queryClient.invalidateQueries({
        queryKey: v2GetPortfolioQueueOptions({ path: { id: startupId } }).queryKey,
      }),
      invalidateHome(),
    ])
  }

  const [archiving, setArchiving] = useState<Record<string, boolean>>({})
  const archivePortfolioMessage = useArchivePortfolioMessageMutation(startupId)
  const skipAllPortfolio = useSkipAllPortfolioQueuedMutation(startupId)
  const archiveMessage = (entry: RosterMatch) => {
    const messageId = entry.leadId
    if (!messageId) return
    setArchiving((p) => ({ ...p, [messageId]: true }))
    archivePortfolioMessage.mutate(messageId, {
      onSettled: () =>
        setArchiving((p) => {
          const next = { ...p }
          delete next[messageId]
          return next
        }),
    })
  }

  // Effective state = in-flight optimistic, else the server's approvalState.
  const stateOf = (entry: RosterMatch): ResolveState | undefined =>
    (entry.leadId ? resolutions[entry.leadId] : undefined) ?? entry.approvalState
  const pendingEntries = queued.filter((e) => stateOf(e) === undefined && e.contactId)
  const pendingCount = pendingEntries.length

  const decide = (action: 'approve' | 'decline') => (entry: RosterMatch) => {
    const leadId = entry.leadId
    const contactId = entry.contactId
    if (!leadId || !contactId) return
    setResolutions((p) => ({ ...p, [leadId]: action === 'approve' ? 'approving' : 'declining' }))
    outreachApi
      .portfolioSetContactDecisions(startupId, [
        { contactId, decision: action === 'approve' ? 'APPROVED' : 'DECLINED' },
      ])
      .then(() => {
        setResolutions((p) => ({
          ...p,
          [leadId]: action === 'approve' ? 'approved' : 'declined',
        }))
        invalidateQueue()
      })
      .catch(() =>
        setResolutions((p) => {
          const next = { ...p }
          delete next[leadId]
          return next
        })
      )
  }

  const approveAll = () => {
    const ids = pendingEntries
      .map((e) => ({ leadId: e.leadId, contactId: e.contactId }))
      .filter((x): x is { leadId: string; contactId: string } => !!x.leadId && !!x.contactId)
    if (ids.length === 0) return
    setResolutions((p) => {
      const next = { ...p }
      for (const { leadId } of ids) next[leadId] = 'approving'
      return next
    })
    outreachApi
      .portfolioSetContactDecisions(
        startupId,
        ids.map(({ contactId }) => ({ contactId, decision: 'APPROVED' as const }))
      )
      .then(() => {
        setResolutions((p) => {
          const next = { ...p }
          for (const { leadId } of ids) next[leadId] = 'approved'
          return next
        })
        invalidateQueue()
      })
      .catch(() =>
        setResolutions((p) => {
          const next = { ...p }
          for (const { leadId } of ids) delete next[leadId]
          return next
        })
      )
  }

  return (
    // Same anchor as the investor roster so the hero's "Contacts to reach →"
    // scrolls here too; only one roster is mounted at a time, so the shared id
    // never collides in the DOM.
    <section id={CONTACTS_BOARD_ID} className="mb-10 scroll-mt-24 space-y-5">
      <RosterHeader startupId={startupId} />

      <RosterKanban
        columns={[
          {
            tone: 'primary',
            title: 'Queued',
            // Shared anchor with the investor queued column so the top "Review now"
            // banner can scroll here when it routes to the portfolio tab.
            anchorId: QUEUED_COLUMN_ID,
            subtitle: isResearching
              ? portfolioStatus.contactsCount === 0
                ? 'Finding founder contacts across investor portfolios…'
                : `${portfolioStatus.contactsCount} founders found. Reviewing messages before launch.`
              : pendingCount > 0
                ? 'Awaiting your approval · approve a founder to start their outreach.'
                : "Approved and queued to send · connection request hasn't gone out yet.",
            totalCount:
              isResearching || portfolioStatus.researchInProgress || contactsEstimated
                ? `~${isResearching ? portfolioStatus.contactsCount : queued.length}`
                : queued.length,
            // Founders can be approved/declined as soon as they appear — even
            // while still researching (the queue previews carry real contact ids,
            // so the decision persists). Nothing sends until the campaign is built
            // and admin-approved; early decisions just pre-stage the send list.
            entries: queued,
            hideStatePill: true,
            onCardClick: setDetail,
            onApprove: decide('approve'),
            onDecline: decide('decline'),
            onApproveAll: pendingCount > 0 ? approveAll : undefined,
            approveAllPending: pendingCount,
            onSkipAll: pendingCount > 0 ? () => skipAllPortfolio.mutate() : undefined,
            skippingAll: skipAllPortfolio.isPending,
            resolutions,
            footer: isResearching ? (
              <div className="flex flex-col items-center gap-3 px-4 py-8 text-center">
                <Loader2 className="size-5 animate-spin text-muted-foreground/60" />
                <p className="text-[12px] text-muted-foreground">
                  {portfolioStatus.contactsCount === 0
                    ? 'Scanning investor portfolios for founder contacts…'
                    : `Found ${portfolioStatus.contactsCount} founders. The campaign launches after message review.`}
                </p>
              </div>
            ) : undefined,
          },
          {
            tone: 'blue',
            title: 'In progress',
            subtitle: 'Sequence is sending: connection requested or in follow-up.',
            totalCount: active.length,
            entries: active,
            onCardClick: setDetail,
          },
          {
            tone: 'muted',
            title: 'Finished',
            subtitle: 'Sequence completed, no warm intro · archive to tidy up.',
            totalCount: finished.length,
            entries: finished,
            hideStatePill: true,
            onCardClick: setDetail,
            onArchive: archiveMessage,
            archiving,
          },
        ]}
      />

      <QueuedApprovalDialog
        investor={detail}
        messages={detail?.previewMessages ?? null}
        onClose={() => setDetail(null)}
        onApprove={(entry) => {
          decide('approve')(entry)
          setDetail(null)
        }}
        onDecline={(entry) => {
          decide('decline')(entry)
          setDetail(null)
        }}
        onRemove={() => setDetail(null)}
      />
    </section>
  )
}

function PortfolioFeatureToggleCard({ startupId }: { startupId: string }) {
  const queryClient = useQueryClient()
  const [pending, setPending] = useState(false)

  const enable = async () => {
    setPending(true)
    try {
      await v2EnablePortfolioOutreach({ path: { id: startupId } })
      await queryClient.invalidateQueries({
        queryKey: v2GetPortfolioOutreachStatusOptions({ path: { id: startupId } }).queryKey,
      })
      trackEvent('portfolio_outreach_enabled', { startup_id: startupId })
    } finally {
      setPending(false)
    }
  }

  return (
    <div className="mb-6 flex items-start gap-4 rounded-2xl border border-primary/20 bg-primary/5 px-6 py-5">
      <div className="flex-1">
        <p className="text-[15px] font-semibold text-foreground">Reach out to portfolio founders</p>
        <p className="mt-1 text-[13px] text-muted-foreground">
          Contact founders backed by your matched investors and ask for a warm intro to the fund.
        </p>
        <p className="mt-2 text-[12px] text-muted-foreground/80">
          Your daily LinkedIn capacity is shared between both campaigns.
        </p>
      </div>
      <CtaButton className="mt-0.5" loading={pending} onClick={enable}>
        {pending ? 'Activating…' : 'Activate'}
      </CtaButton>
    </div>
  )
}

// ════════════════════════════════════════════════════════════════════════════
// Hero
// ════════════════════════════════════════════════════════════════════════════

function ActiveHero({
  startupId,
  variant,
  channel,
  isLaunched,
  linkedinConnected,
  campaignLabel,
  actions,
}: {
  startupId: string
  variant: ActiveVariant
  /** Campaign on screen — decides the pre-launch headline. */
  channel: ChannelId
  isLaunched: boolean
  /** Sender connected (pre-launch too) — surfaces the top-right Disconnect button. */
  linkedinConnected: boolean
  /** Name of the campaign this page is showing — leads the eyebrow once launched. */
  campaignLabel?: string
  /** That campaign's pause/resume + edit-messages controls, next to Disconnect. */
  actions?: ReactNode
}) {
  const config = HERO_CONFIG[variant]
  // Only the setup state sells a campaign; every other variant reports on one
  // that already exists, where the state matters and the channel does not.
  const copy = variant === 'setup' ? SETUP_COPY_BY_CHANNEL[channel] : config
  const [topupOpen, setTopupOpen] = useState(false)
  const isBudgetPaused = variant === 'paused-budget'

  return (
    <>
      <header className="relative mb-6 rounded-2xl border border-border/40 bg-gradient-to-br from-card/40 to-card/10 px-7 py-8">
        {(isLaunched || linkedinConnected || actions) && (
          <div className="absolute right-5 top-5 flex flex-wrap items-center justify-end gap-2">
            {actions}
            {isBudgetPaused && (
              <CtaButton
                size="sm"
                arrow
                onClick={() => {
                  trackEvent('outreach_topup_opened', {
                    startup_id: startupId,
                    reason: 'budget_paused',
                  })
                  setTopupOpen(true)
                }}
              >
                Top up to resume
              </CtaButton>
            )}
            {/* Single shared LinkedIn connection — disconnect lives in the hero's
                top-right corner, above both channels. */}
            {(isLaunched || linkedinConnected) && (
              <DisconnectLinkedInButton startupId={startupId} variant="secondary" />
            )}
          </div>
        )}
        <div className="max-w-2xl">
          <div
            className={cn(
              'mb-2 inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.14em]',
              config.eyebrowColor
            )}
          >
            {config.icon}
            {campaignLabel ?? config.eyebrowPrefix} · {config.eyebrowState}
          </div>
          <h1 className="text-[34px] font-bold leading-[1.1] tracking-[-0.02em] text-foreground">
            {copy.headline}
          </h1>
          <p className="mt-3 text-[14.5px] leading-relaxed text-foreground/80">{copy.sub}</p>
        </div>
      </header>
      <OutreachTopUpDialog startupId={startupId} open={topupOpen} onOpenChange={setTopupOpen} />
    </>
  )
}

/**
 * Pre-launch hero copy, per campaign. The two campaigns do different things —
 * one messages the investors, the other messages founders those investors
 * already backed — so the page that is selling them cannot describe both with
 * one sentence. Once a campaign is launched the headline becomes about its
 * state (running, paused, out of balance), which reads the same either way.
 */
const SETUP_COPY_BY_CHANNEL: Record<ChannelId, { headline: ReactNode; sub: string }> = {
  investors: {
    headline: (
      <>
        Personalized LinkedIn intros to{' '}
        <span className="text-primary">investors matched to your startup.</span>
      </>
    ),
    sub: 'Sent from your own LinkedIn, approved by you first. You only pay when an investor views your profile or books a call.',
  },
  portfolio: {
    headline: (
      <>
        Warm intros to your matched investors.{' '}
        <span className="text-primary">From the founders they already backed.</span>
      </>
    ),
    sub: "Personalized LinkedIn messages to founders in investors' portfolios, asking them for a warm intro to the investor. Sent from your account. You only pay when a founder offers the intro.",
  },
}

const HERO_CONFIG: Record<
  ActiveVariant,
  {
    icon: ReactNode
    /** Leading noun. Overridden by the active campaign's name once launched. */
    eyebrowPrefix: string
    eyebrowState: string
    eyebrowColor: string
    headline: ReactNode
    sub: string
  }
> = {
  setup: {
    icon: <Sparkles className="size-3" />,
    // Eyebrow now carries the key trust differentiator, not a filler feature name.
    eyebrowPrefix: 'Outreach',
    eyebrowState: 'you approve every message',
    // Muted eyebrow keeps the orange budget for the CTA + the headline accent.
    eyebrowColor: 'text-muted-foreground',
    // Pre-launch the headline is the pitch for the campaign on screen, so it
    // comes from SETUP_COPY_BY_CHANNEL rather than from here. These two are the
    // investors wording, kept as the fallback for the shared shape.
    headline: SETUP_COPY_BY_CHANNEL.investors.headline,
    sub: SETUP_COPY_BY_CHANNEL.investors.sub,
  },
  'awaiting-campaign': {
    icon: <Loader2 className="size-3 animate-spin" />,
    eyebrowPrefix: 'Campaign',
    eyebrowState: 'setting up',
    eyebrowColor: 'text-primary',
    headline: (
      <>
        Campaign is being prepared. <span className="text-primary">Approve who we reach.</span>
      </>
    ),
    sub: "We're building your campaign and warming up your sender. Sending starts once that is done and you have approved the contacts below, so nobody hears from you before you say so.",
  },
  'awaiting-sender': {
    icon: <span className="size-2 rounded-full bg-amber-400" />,
    eyebrowPrefix: 'Campaign',
    eyebrowState: 'action needed',
    eyebrowColor: 'text-amber-400',
    headline: (
      <>
        Reconnect your LinkedIn. <span className="text-primary">One-time, about a minute.</span>
      </>
    ),
    sub: 'Your LinkedIn sender needs reconnecting before sends can continue. Everything else stays as it is: matches, messages and approvals.',
  },
  'awaiting-contacts': {
    icon: <span className="size-2 rounded-full bg-amber-400" />,
    eyebrowPrefix: 'Campaign',
    eyebrowState: 'pending contact approval',
    eyebrowColor: 'text-amber-400',
    headline: (
      <>
        Your campaign is ready. <span className="text-primary">Approve who we reach.</span>
      </>
    ),
    sub: 'Nothing sends until you approve a contact. Approve anyone on the roster below and messages start going out from your LinkedIn, gradually.',
  },
  ready: {
    icon: <span className="size-2 animate-pulse rounded-full bg-emerald-400" />,
    eyebrowPrefix: 'Campaign',
    eyebrowState: 'running',
    eyebrowColor: 'text-emerald-400',
    headline: (
      <>
        Your campaign is <span className="text-primary">live.</span>
      </>
    ),
    sub: 'Sequences are sending from your LinkedIn. Replies land in your normal inbox, and we surface the ones worth your time.',
  },
  paused: {
    icon: <span className="size-2 rounded-full bg-amber-400" />,
    eyebrowPrefix: 'Campaign',
    eyebrowState: 'paused',
    eyebrowColor: 'text-amber-400',
    headline: (
      <>
        Campaign paused. <span className="text-primary">Resume anytime.</span>
      </>
    ),
    sub: 'No sends are going out. The sequences are queued and will pick up where they left off when you resume.',
  },
  'paused-budget': {
    icon: <span className="size-2 rounded-full bg-amber-400" />,
    eyebrowPrefix: 'Campaign',
    eyebrowState: 'auto-paused',
    eyebrowColor: 'text-amber-400',
    headline: (
      <>
        Out of balance. <span className="text-primary">Top up to resume sending.</span>
      </>
    ),
    sub: 'Your wallet ran out mid-campaign. Resume in seconds by topping up, and sequences pick up right where they left off.',
  },
  error: {
    icon: <span className="size-2 rounded-full bg-red-400" />,
    eyebrowPrefix: 'Campaign',
    eyebrowState: 'error',
    eyebrowColor: 'text-red-400',
    headline: (
      <>
        Something needs attention. <span className="text-primary">Check the status below.</span>
      </>
    ),
    sub: 'The campaign hit an error. Details are in the status row. Most resolve once you reconnect LinkedIn or top up.',
  },
}

function FunnelGrid({
  kpi,
  funnel,
}: {
  kpi: {
    investorsContacted: number
    accepted: number
    profileViews: number
    returnVisits: number
    replied: number
    callsBooked: number
    acceptanceRatePct: number | null
    replyRatePct: number | null
    costPerCallCents: number | null
  }
  funnel: { reachedOut: number }
}) {
  return (
    <section className="mb-10">
      <div className="mb-3 text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
        Funnel at a glance
      </div>
      <div className="grid grid-cols-2 gap-px overflow-hidden rounded-xl border border-border/40 bg-border/40 md:grid-cols-6">
        <StatCell
          size="sm"
          value={String(funnel.reachedOut)}
          label="Reached out"
          sub="Connection requests sent"
        />
        <StatCell
          size="sm"
          value={String(kpi.accepted)}
          label="Accepted"
          sub={kpi.acceptanceRatePct != null ? `${kpi.acceptanceRatePct}% acceptance rate` : '—'}
        />
        <StatCell
          size="sm"
          value={String(kpi.replied)}
          label="Replies"
          sub={kpi.replyRatePct != null ? `${kpi.replyRatePct}% of accepted` : '—'}
        />
        <StatCell
          size="sm"
          value={String(kpi.profileViews)}
          label="Profile views"
          sub="20s+ active sessions"
        />
        <StatCell
          size="sm"
          value={String(kpi.returnVisits)}
          label="Repeated views"
          sub="Came back to read more"
        />
        <StatCell
          size="sm"
          value={String(kpi.callsBooked)}
          label="Calls booked"
          sub={
            kpi.costPerCallCents != null
              ? `${formatUsd(kpi.costPerCallCents)} avg cost per call`
              : '—'
          }
          accent
        />
      </div>
    </section>
  )
}

type PortfolioQueueData = Extract<V2GetPortfolioQueueResponse, { status: 'ok' }>['data']
type PortfolioQueueRow = PortfolioQueueData['queued'][number]

// Portfolio funnel — computed from the portfolio queue (no calls; the final
// accent cell is "Warm intros" confirmed). Mirrors FunnelGrid's look so the
// numbers visibly swap when toggling between the Investors / Portfolio tabs.
function PortfolioFunnelGrid({
  queue,
}: {
  queue: {
    queued: PortfolioQueueRow[]
    active: PortfolioQueueRow[]
    warmIntro: PortfolioQueueRow[]
    finished: PortfolioQueueRow[]
  } | null
}) {
  const all: PortfolioQueueRow[] = queue
    ? [...queue.queued, ...queue.active, ...queue.warmIntro, ...queue.finished]
    : []
  const reachedOut = all.filter((m) => m.connectionRequestSentAt != null).length
  const accepted = all.filter((m) => m.connectionAcceptedAt != null).length
  const replied = all.filter((m) => m.firstReplyAt != null).length
  const confirmed = all.filter((m) => m.warmIntroConfirmedAt != null).length
  const acceptanceRatePct = reachedOut > 0 ? Math.round((accepted / reachedOut) * 100) : null
  const replyRatePct = accepted > 0 ? Math.round((replied / accepted) * 100) : null

  return (
    <section className="mb-10">
      <div className="mb-3 text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
        Funnel at a glance
      </div>
      <div className="grid grid-cols-2 gap-px overflow-hidden rounded-xl border border-border/40 bg-border/40 md:grid-cols-4">
        <StatCell value={String(reachedOut)} label="Reached out" sub="Connection requests sent" />
        <StatCell
          value={String(accepted)}
          label="Accepted"
          sub={acceptanceRatePct != null ? `${acceptanceRatePct}% acceptance rate` : '—'}
        />
        <StatCell
          value={String(replied)}
          label="Replies"
          sub={replyRatePct != null ? `${replyRatePct}% of accepted` : '—'}
        />
        <StatCell
          value={String(confirmed)}
          label="Warm intros offered"
          sub="Confirmed introductions"
          accent
        />
      </div>
    </section>
  )
}

// ════════════════════════════════════════════════════════════════════════════
// Investor roster (Engaged / In progress / Queued / Finished)
// ════════════════════════════════════════════════════════════════════════════

type RosterGroupKey = 'engaged' | 'in_progress' | 'queued' | 'finished'

/** Flow progress for an in-progress contact — powers the sequence stepper. */
interface RosterProgress {
  viewedProfileAt: string | null
  likedPostAt: string | null
  connectionRequestSentAt: string | null
  sentAt: string | null
  acceptedAt: string | null
  views: number
  returnVisits: number
}

export interface RosterMatch {
  investorId: string
  investorName: string
  /** InvestorContact id — keys the pre-launch approve/decline decision. Absent on fund-level placeholders. */
  contactId?: string
  contactName: string | null
  contactTitle: string | null
  contactScope: string | null
  group: RosterGroupKey
  /** Short label for the kanban pill / table status column. */
  stage: string
  /** Why a finished contact stopped — drives the Finished-card resolution tooltip. */
  finishedReason?: 'exhausted' | 'fund_engaged' | null
  charges: { views: number; returnVisits: number; calls: number }
  lastReply?: string
  /**
   * Where the investor asked to be reached instead — the address itself, so
   * the row says what to do rather than that there is something to do.
   */
  redirect?: { url: string | null; email: string | null }
  /** The message row behind this entry — what gets marked read. */
  messageId?: string
  /** True while the founder has not read this reply yet. Drives the unread dot. */
  unread?: boolean
  lastEventAt?: string
  /** Set on queued-for-approval entries so the card can link out to LinkedIn. */
  linkedinUrl?: string
  /**
   * Fund-level placeholder: the fund is matched but its GP contacts haven't been
   * researched yet, so the card stands in for "a partner at this fund, TBD".
   */
  contactPending?: boolean
  /** Activity research for this fund's contacts is currently running. */
  researchInProgress?: boolean
  /** OutreachLead id — present on queued entries, used to approve/decline. */
  leadId?: string
  /** Server-persisted decision for a queued entry; undefined while awaiting. */
  approvalState?: 'approved' | 'declined'
  /** Rich investor detail, shown brief on the card and in full in the dialog. */
  profile?: QueuedProfile
  /** Flow progress; set on in-progress entries to drive the sequence stepper. */
  progress?: RosterProgress
  /** Contact kind — drives the colored kind badge on the card. */
  kind?: 'investor' | 'warm_intro'
  /** Portfolio founder fields — company the founder is at and fund that backs them. */
  companyName?: string
  /** Link to the portfolio company (LinkedIn / Crunchbase), shown in the detail dialog. */
  companyUrl?: string | null
  fundName?: string
  /** Per-founder resolved msg1/2/3 — used as the detail dialog's conversation preview. */
  previewMessages?: {
    msg1: string | null
    msg2: string | null
    msg3: string | null
    // Prompt version per message — decides legacy-vs-v2 rendering in the dialog.
    msg1PromptVersion?: string | null
    msg2PromptVersion?: string | null
    msg3PromptVersion?: string | null
  }
}

interface QueuedProfile {
  fitScore: number | null
  description: string
  stages: string[]
  sectors: string[]
  geographies: string[]
  ticket: string
  reasoning: string
  portfolio: string[]
}

// Per-lead approval state tracked client-side: `*ing` while the mutation is in
// flight, terminal once it resolves. Resolved cards stay visible (with a badge)
// even after the server drops them from the AWAITING_APPROVAL queue.
type ResolveState = 'approving' | 'declining' | 'approved' | 'declined'

// Founder-facing copy for why a finished contact stopped — short pill label +
// the tooltip that explains it.
const FINISHED_REASON_COPY: Record<
  'exhausted' | 'fund_engaged',
  { label: string; tooltip: string }
> = {
  exhausted: {
    label: 'No response',
    tooltip:
      'We ran the full message sequence, connection request and follow-ups, but this contact never replied.',
  },
  fund_engaged: {
    label: 'Another contact replied',
    tooltip:
      'Someone else at this fund already responded, so we stopped reaching out here to avoid double-touching the firm.',
  },
}

// Maps the real outreach-queue API item to the roster card/dialog shape,
// turning the investor's raw enums into the display labels the matches tab uses.
export function queueItemToRoster(item: OutreachQueueItem): RosterMatch {
  const label = (map: Record<string, string>, value: string) => map[value] ?? value
  const isFinished = item.status === 'finished'
  const isInProgress = item.status === 'in_progress'
  const isEngaged = item.status === 'engaged'
  const progress = item.progress ?? null
  const group: RosterGroupKey = isFinished
    ? 'finished'
    : isInProgress
      ? 'in_progress'
      : isEngaged
        ? 'engaged'
        : 'queued'
  const stage = isFinished
    ? 'No response'
    : isEngaged
      ? progress?.callBooked
        ? 'Call booked'
        : 'Replied'
      : isInProgress
        ? progress?.acceptedAt
          ? 'Connected & awaiting reply'
          : progress?.connectionRequestSentAt
            ? 'Reached out'
            : 'Warming up'
        : 'Awaiting approval'
  return {
    investorId: item.investor.id,
    leadId: item.leadId,
    approvalState: item.status === 'approved' ? 'approved' : undefined,
    investorName: item.investor.name,
    contactName: item.contact.name,
    contactTitle: item.contact.title,
    contactScope: item.contact.scope,
    group,
    stage,
    finishedReason: item.finishedReason,
    charges: {
      views: progress?.views ?? 0,
      returnVisits: progress?.returnVisits ?? 0,
      calls: progress?.callBooked ? 1 : 0,
    },
    lastReply: progress?.replySummary ?? undefined,
    messageId: progress?.messageId ?? undefined,
    unread: progress?.repliedAt != null && progress?.seenAt == null,
    lastEventAt: progress?.repliedAt ?? undefined,
    progress: isInProgress && progress ? progress : undefined,
    linkedinUrl: item.contact.linkedinUrl ?? undefined,
    profile: {
      fitScore: item.investor.fitScore,
      description: item.investor.description ?? '',
      stages: item.investor.stages.map((s) => label(STAGE_LABELS, s)),
      sectors: item.investor.sectors.map((s) => label(SECTOR_LABELS, s)),
      geographies: item.investor.geographies.map((g) => label(GEO_LABELS, g)),
      ticket: formatTicket(item.investor.minTicket, item.investor.maxTicket) ?? '',
      reasoning: item.investor.reasoning ?? '',
      portfolio: item.investor.portfolio,
    },
  }
}

// Anchor for the "Review now" banner CTA to scroll the founder to the Queued column.
const QUEUED_COLUMN_ID = 'outreach-queued-column'

// Anchor for the contacts board (pre-launch preview or live roster), where
// each card opens the per-contact messages.
export const CONTACTS_BOARD_ID = 'outreach-contacts-board'

// Roster section header with the Archive page link — rendered for both the
// populated board and the empty state, so the Archive stays reachable.
function RosterHeader({ startupId }: { startupId: string }) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-3">
      <div>
        <h2 className="text-[18px] font-bold tracking-[-0.01em] text-foreground">
          Campaign roster
        </h2>
        <p className="mt-0.5 text-[13px] text-muted-foreground">
          Who's been contacted and where each investor sits in the sequence.
        </p>
      </div>
      <Link
        to="/app/startup/$id/outreach/archive"
        params={{ id: startupId }}
        className={secondaryLinkClasses({ size: 'sm' })}
      >
        <Archive />
        <span>Show archived</span>
      </Link>
    </div>
  )
}

function InvestorRoster({
  startupId,
  variant,
  contactsEstimated,
}: {
  startupId: string
  variant: ActiveVariant
  contactsEstimated: boolean
}) {
  const messagesQuery = useOutreachMessagesQuery(startupId)
  const messages = messagesQuery.data ?? null
  const queueQuery = useOutreachQueueQuery(startupId)
  const approve = useResolveQueuedMutation(startupId, 'approve')
  const decline = useResolveQueuedMutation(startupId, 'decline')
  const approveAllMutation = useApproveAllQueuedMutation(startupId)
  const skipAllMutation = useSkipAllQueuedMutation(startupId)
  const archiveContact = useArchiveContactMutation(startupId)
  const removeInProgressLead = useRemoveInProgressLeadMutation(startupId)
  const [detail, setDetail] = useState<RosterMatch | null>(null)
  // In-flight optimistic states only ('approving'/'declining'); the terminal
  // approved state is the server's, carried on each entry's approvalState (so it
  // survives a refresh). Declined contacts leave the queue (they're archived).
  const [resolutions, setResolutions] = useState<Record<string, ResolveState>>({})
  // Leads with an in-flight archive, hidden optimistically from the Finished column.
  const [archiving, setArchiving] = useState<Record<string, boolean>>({})

  // Queued = contacts still on the approval board (awaiting or approved). Finished
  // = contacts whose sequence ran with no response. Both are lead-based (from the
  // queue endpoint); declined/archived contacts live on the Archive page.
  const queueItems = useMemo(
    () => (queueQuery.data ?? []).map(queueItemToRoster),
    [queueQuery.data]
  )
  const queued = queueItems.filter((r) => r.group === 'queued')
  const inProgress = queueItems.filter((r) => r.group === 'in_progress')
  const finished = queueItems.filter((r) => r.group === 'finished' && !archiving[r.leadId ?? ''])

  // Effective state = the in-flight optimistic one, else the server's.
  const stateOf = (entry: RosterMatch): ResolveState | undefined =>
    (entry.leadId ? resolutions[entry.leadId] : undefined) ?? entry.approvalState
  // Contacts still awaiting a decision — drives the header count and "Approve all".
  const pendingEntries = queued.filter((entry) => {
    const state = stateOf(entry)
    return entry.leadId && state !== 'approved' && state !== 'declined'
  })
  const pendingCount = pendingEntries.length

  // Render the section even with an empty board — it carries the only link to
  // the Archive page, which must stay reachable after every contact has been
  // declined or archived (declined/archived leads live there, not here).
  if (queueItems.length === 0) {
    return (
      <section id={CONTACTS_BOARD_ID} className="mb-10 scroll-mt-24 space-y-5">
        <RosterHeader startupId={startupId} />
        <div className="rounded-xl border border-dashed border-border/40 px-5 py-8 text-center text-[13px] text-muted-foreground">
          No contacts on the board yet. They show up here as your campaign queues them. Declined and
          archived contacts live in the Archive.
        </div>
      </section>
    )
  }

  const isPaused = variant === 'paused' || variant === 'paused-budget'
  const resolve = (action: 'approve' | 'decline') => (entry: RosterMatch) => {
    const leadId = entry.leadId
    if (!leadId) return
    setResolutions((prev) => ({
      ...prev,
      [leadId]: action === 'approve' ? 'approving' : 'declining',
    }))
    const mutation = action === 'approve' ? approve : decline
    mutation.mutate(leadId, {
      onSuccess: () => {
        trackEvent(action === 'approve' ? 'outreach_lead_approved' : 'outreach_lead_declined', {
          startup_id: startupId,
          investor_id: entry.investorId,
        })
        setResolutions((prev) => ({
          ...prev,
          [leadId]: action === 'approve' ? 'approved' : 'declined',
        }))
      },
      onError: () => {
        // Drop the optimistic state; the card falls back to the server's.
        setResolutions((prev) => {
          const next = { ...prev }
          delete next[leadId]
          return next
        })
      },
    })
  }
  // Approve every contact still awaiting a decision in one request. We optimistically
  // flip each pending card to "approving", then a single bulk call settles them — the
  // queue refetch (on success) carries the server's approved state back.
  const approveAll = () => {
    if (pendingEntries.length === 0) return
    const leadIds = pendingEntries.map((entry) => entry.leadId).filter((id): id is string => !!id)
    setResolutions((prev) => {
      const next = { ...prev }
      for (const id of leadIds) next[id] = 'approving'
      return next
    })
    approveAllMutation.mutate(undefined, {
      onSuccess: () => {
        for (const entry of pendingEntries) {
          trackEvent('outreach_lead_approved', {
            startup_id: startupId,
            investor_id: entry.investorId,
          })
        }
        setResolutions((prev) => {
          const next = { ...prev }
          for (const id of leadIds) next[id] = 'approved'
          return next
        })
      },
      onError: () => {
        // Drop the optimistic states; cards fall back to the server's.
        setResolutions((prev) => {
          const next = { ...prev }
          for (const id of leadIds) delete next[id]
          return next
        })
      },
    })
  }
  // Open the approval dialog for a queued lead, recording the review.
  const reviewLead = (entry: RosterMatch) => {
    trackEvent('outreach_lead_reviewed', {
      startup_id: startupId,
      investor_id: entry.investorId,
    })
    setDetail(entry)
  }
  // From the dialog: resolve then close (the card reflects the new state).
  const resolveFromDialog = (action: 'approve' | 'decline') => (entry: RosterMatch) => {
    resolve(action)(entry)
    setDetail(null)
  }
  const removeFromDialog = (entry: RosterMatch) => {
    const leadId = entry.leadId
    if (!leadId) return
    setDetail(null)
    removeInProgressLead.mutate(leadId)
  }
  const archive = (entry: RosterMatch) => {
    const leadId = entry.leadId
    if (!leadId) return
    setArchiving((prev) => ({ ...prev, [leadId]: true }))
    archiveContact.mutate(leadId, {
      onSuccess: () =>
        trackEvent('outreach_lead_archived', {
          startup_id: startupId,
          investor_id: entry.investorId,
        }),
      onSettled: () =>
        setArchiving((prev) => {
          const next = { ...prev }
          delete next[leadId]
          return next
        }),
    })
  }

  return (
    <section id={CONTACTS_BOARD_ID} className="mb-10 scroll-mt-24 space-y-5">
      <RosterHeader startupId={startupId} />

      <RosterKanban
        columns={[
          {
            tone: 'primary',
            title: 'Queued',
            showRecommended: true,
            totalCount: contactsEstimated ? `~${queued.length}` : queued.length,
            subtitle:
              pendingCount > 0
                ? 'Awaiting your approval · tap a card to review, or approve/decline here'
                : 'Approved and queued to send · tap a card to change your decision',
            entries: queued,
            onCardClick: reviewLead,
            onApprove: resolve('approve'),
            onDecline: resolve('decline'),
            // Live board keeps the "only when something's pending" gate — an
            // Approve-all on an already-resolved queue would be a no-op.
            onApproveAll: pendingCount > 0 ? approveAll : undefined,
            approveAllPending: pendingCount,
            approvingAll: approveAllMutation.isPending,
            onSkipAll: pendingCount > 0 ? () => skipAllMutation.mutate() : undefined,
            skippingAll: skipAllMutation.isPending,
            resolutions,
            anchorId: QUEUED_COLUMN_ID,
            incremental: true,
          },
          {
            tone: 'blue',
            title: 'In progress',
            totalCount: inProgress.length,
            subtitle: isPaused
              ? 'Paused mid-sequence'
              : 'Sequence in flight · each card shows where it sits',
            entries: inProgress,
            onCardClick: reviewLead,
          },
          {
            tone: 'muted',
            title: 'Finished',
            totalCount: finished.length,
            subtitle: 'No response · sequence ran end-to-end · archive to tidy up',
            entries: finished,
            onArchive: archive,
            archiving,
          },
        ]}
      />

      <QueuedApprovalDialog
        investor={detail}
        messages={messages}
        onClose={() => setDetail(null)}
        onApprove={resolveFromDialog('approve')}
        onDecline={resolveFromDialog('decline')}
        onRemove={removeFromDialog}
      />
    </section>
  )
}

export function QueuedApprovalDialog({
  investor,
  messages,
  track = 'investor',
  onClose,
  onApprove,
  onDecline,
  onRemove,
}: {
  investor: RosterMatch | null
  messages: {
    msg1: string | null
    msg2: string | null
    msg3: string | null
    msg2Cta?: string | null
    msg1PromptVersion?: string | null
    msg2PromptVersion?: string | null
    msg3PromptVersion?: string | null
    // Present once the startup has A/B versions; this preview shows one of them.
    variants?: {
      msg1: OutreachMessageVariant[]
      msg2: OutreachMessageVariant[]
      msg3: OutreachMessageVariant[]
    }
  } | null
  // Outreach track — decides the legacy-vs-v2 version cutoffs for this dialog's
  // preview. Portfolio and investor prompts version independently.
  track?: OutreachTrack
  onClose: () => void
  onApprove: (entry: RosterMatch) => void
  onDecline: (entry: RosterMatch) => void
  onRemove: (entry: RosterMatch) => void
}) {
  return (
    <Dialog open={investor !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="flex max-h-[85vh] flex-col gap-0 overflow-hidden p-0 sm:max-w-4xl lg:max-w-5xl">
        {investor && (
          <QueuedApprovalBody
            key={investor.investorId}
            investor={investor}
            messages={messages}
            track={track}
            onApprove={onApprove}
            onDecline={onDecline}
            onRemove={onRemove}
          />
        )}
      </DialogContent>
    </Dialog>
  )
}

function QueuedApprovalBody({
  investor,
  messages,
  track,
  onApprove,
  onDecline,
  onRemove,
}: {
  investor: RosterMatch
  messages: {
    msg1: string | null
    msg2: string | null
    msg3: string | null
    // Legacy msg2 CTA, folded into the msg2 decoration for legacy rows.
    msg2Cta?: string | null
    // Langfuse prompt version per message — decides legacy-vs-v2 rendering.
    msg1PromptVersion?: string | null
    msg2PromptVersion?: string | null
    msg3PromptVersion?: string | null
    // Present once the startup has A/B versions; this preview shows one of them.
    variants?: {
      msg1: OutreachMessageVariant[]
      msg2: OutreachMessageVariant[]
      msg3: OutreachMessageVariant[]
    }
  } | null
  track: OutreachTrack
  onApprove: (entry: RosterMatch) => void
  onDecline: (entry: RosterMatch) => void
  onRemove: (entry: RosterMatch) => void
}) {
  const name = investor.contactName ?? investor.investorName
  const firstName = (investor.contactName ?? '').split(' ')[0] ?? ''
  // Each message is a self-contained template carrying [firstName]/[FundName]/
  // [FundFocus]/[FundSector]/[Link] inline; substitute them so the preview
  // matches the send. fundName falls back to investorName (the board roster
  // carries the fund as investorName).
  const resolve = {
    firstName,
    fundName: investor.fundName || investor.investorName || undefined,
    ...fundTokensFromProfile(investor.profile?.stages, investor.profile?.sectors),
  }
  const versionByField = {
    msg1: messages?.msg1PromptVersion,
    msg2: messages?.msg2PromptVersion,
    msg3: messages?.msg3PromptVersion,
  }
  // Legacy-vs-v2 is decided by the prompt VERSION (mirrors the backend push).
  // Legacy rows are decorated into the new-template token shape; v2 pass through.
  const previewText = (field: 'msg1' | 'msg2' | 'msg3', text: string | null | undefined) =>
    isLegacyOutreachField(field, versionByField[field], track)
      ? decorateLegacyOutreachPreview(
          field,
          text ?? '',
          field === 'msg2' ? messages?.msg2Cta : null
        )
      : (text ?? '')
  const approveClass = 'bg-emerald-600 text-white hover:bg-emerald-600/90'

  return (
    <>
      <DialogHeader className="border-b border-border/40 px-6 py-5 pr-12 text-left">
        <div className="flex items-start gap-3">
          <div className="flex size-11 shrink-0 items-center justify-center rounded-lg border border-border/50 bg-card text-[13px] font-bold text-foreground/80">
            {makeInitials(name)}
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[10.5px] font-semibold uppercase tracking-wider text-muted-foreground/70">
              {investor.group === 'in_progress'
                ? 'In progress'
                : investor.group === 'finished'
                  ? 'Finished'
                  : investor.group === 'engaged'
                    ? 'Warm intro'
                    : 'Approve outreach to'}
            </div>
            <div className="mt-0.5 flex items-center gap-2">
              <DialogTitle className="min-w-0 truncate text-[18px] leading-tight">
                {name}
              </DialogTitle>
              {investor.linkedinUrl && (
                <a
                  href={investor.linkedinUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex shrink-0 items-center gap-1 text-[12.5px] font-medium text-primary hover:text-primary/80"
                >
                  <Linkedin className="size-3.5" />
                  LinkedIn
                  <ExternalLink className="size-3" />
                </a>
              )}
            </div>
            <DialogDescription className="mt-0.5 text-[13px]">
              {[investor.contactTitle, investor.investorName].filter(Boolean).join(' · ') ||
                'Contact'}
            </DialogDescription>
            {investor.contactScope && (
              <p className="mt-1 text-[12px] leading-snug text-muted-foreground/70">
                {investor.contactScope}
              </p>
            )}
            {/* Portfolio founder: the company they run (backed by the fund). */}
            {investor.companyName && (
              <div className="mt-1 flex items-center gap-1.5 text-[12.5px]">
                <span className="text-muted-foreground/70">Portfolio company:</span>
                {investor.companyUrl ? (
                  <a
                    href={investor.companyUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 font-medium text-primary hover:text-primary/80"
                  >
                    {investor.companyName}
                    <ExternalLink className="size-3" />
                  </a>
                ) : (
                  <span className="font-medium text-foreground/85">{investor.companyName}</span>
                )}
              </div>
            )}
          </div>
          {investor.profile?.fitScore != null && (
            <FitChip score={investor.profile.fitScore} size="lg" />
          )}
        </div>
      </DialogHeader>

      <div className="min-h-0 flex-1 overflow-y-auto px-6 py-4">
        <div className="grid gap-4 md:grid-cols-2">
          {investor.profile && (
            <div className="space-y-3 rounded-xl border border-border/50 bg-card/30 p-4">
              <span className="text-[10.5px] font-semibold uppercase tracking-wider text-muted-foreground/70">
                Investor fit
              </span>
              <p className="text-[13px] leading-relaxed text-foreground/85">
                {investor.profile.description}
              </p>
              <div className="grid grid-cols-3 gap-x-4 gap-y-3">
                <ProfileTags label="Stage" tags={investor.profile.stages} />
                <ProfileTags label="Geography" tags={investor.profile.geographies} />
                <ProfileTags label="Check size" tags={[investor.profile.ticket]} />
              </div>
              <ProfileTags label="Sectors" tags={investor.profile.sectors} />
              <div>
                <div className="text-[10.5px] font-semibold uppercase tracking-wider text-muted-foreground/70">
                  Why this match
                </div>
                <p className="mt-1 text-[12.5px] leading-relaxed text-foreground/80">
                  {investor.profile.reasoning}
                </p>
              </div>
              {investor.profile.portfolio.length > 0 && (
                <div>
                  <div className="text-[10.5px] font-semibold uppercase tracking-wider text-muted-foreground/70">
                    Recent portfolio
                  </div>
                  <div className="mt-1.5 flex flex-wrap gap-1">
                    {investor.profile.portfolio.map((company) => (
                      <MiniTag key={company}>{company}</MiniTag>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Portfolio founder context panel — mirrors the investor-fit panel:
              the fund, the company it backs, and why this is a warm path. */}
          {!investor.profile && investor.companyName && (
            <div className="space-y-3 rounded-xl border border-border/50 bg-card/30 p-4">
              <span className="text-[10.5px] font-semibold uppercase tracking-wider text-muted-foreground/70">
                Warm intro context
              </span>
              <div>
                <div className="text-[10.5px] font-semibold uppercase tracking-wider text-muted-foreground/70">
                  Portfolio company
                </div>
                {investor.companyUrl ? (
                  <a
                    href={investor.companyUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-0.5 inline-flex items-center gap-1 text-[13px] font-medium text-primary hover:text-primary/80"
                  >
                    {investor.companyName}
                    <ExternalLink className="size-3" />
                  </a>
                ) : (
                  <p className="mt-0.5 text-[13px] font-medium text-foreground/85">
                    {investor.companyName}
                  </p>
                )}
              </div>
              <div>
                <div className="text-[10.5px] font-semibold uppercase tracking-wider text-muted-foreground/70">
                  Backed by
                </div>
                <p className="mt-0.5 text-[13px] font-medium text-foreground/85">
                  {investor.fundName}
                </p>
              </div>
              <div>
                <div className="text-[10.5px] font-semibold uppercase tracking-wider text-muted-foreground/70">
                  Founder
                </div>
                <p className="mt-0.5 text-[13px] text-foreground/85">
                  {[investor.contactName, investor.contactTitle].filter(Boolean).join(' · ')}
                </p>
              </div>
              <div>
                <div className="text-[10.5px] font-semibold uppercase tracking-wider text-muted-foreground/70">
                  Why this match
                </div>
                <p className="mt-1 text-[12.5px] leading-relaxed text-foreground/80">
                  {investor.contactName
                    ? `${investor.contactName.split(' ')[0]} and ${investor.companyName}`
                    : investor.companyName}{' '}
                  {investor.contactName ? 'are' : 'is'} backed by {investor.fundName}. They could
                  offer you an intro to {investor.fundName}.
                </p>
              </div>
            </div>
          )}

          <div
            className={cn(
              'space-y-3',
              !investor.profile && !investor.companyName && 'md:col-span-2'
            )}
          >
            <div className="text-right text-[10.5px] font-semibold uppercase tracking-wider text-muted-foreground/70">
              The conversation we'll start
            </div>
            <OutreachConversationSequence
              firstName={firstName}
              resolve={resolve}
              connectionText={previewText('msg1', messages?.msg1)}
              followUpText={previewText('msg2', messages?.msg2)}
              secondFollowUpText={previewText('msg3', messages?.msg3)}
            />
            <p className="text-right text-[11px] text-muted-foreground/80">
              Your contact's first name is added automatically to all three messages. Your SeedForge
              Profile link is added to the first two.
            </p>
            {/* With several versions live, this preview is ONE of them — saying
                "the conversation we'll start" without that would be a small lie
                about what this contact receives. A founder edit pins the field
                to their text, so the caveat disappears again. */}
            {(messages?.variants?.msg1?.length ?? 0) > 1 && (
              <p className="text-right text-[11px] text-muted-foreground/80">
                We test a few versions of this sequence. This is one of them — you can see the
                others under Edit messages.
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Approve/decline only while a contact is still awaiting approval (queued).
          In-progress can be removed; finished/engaged are read-only. */}
      {(investor.group === 'queued' || investor.group === 'in_progress') && (
        <DialogFooter className="border-t border-border/40 px-6 py-4 sm:items-center">
          {investor.group === 'in_progress' ? (
            <Button variant="destructive" onClick={() => onRemove(investor)}>
              <X className="size-3.5" />
              Remove from campaign
            </Button>
          ) : (
            <>
              {investor.contactPending && (
                <span className="mr-auto flex items-center gap-1.5 text-[12px] text-muted-foreground">
                  <Loader2 className="size-3.5 shrink-0 animate-spin" />
                  Still finding the best partner to reach…
                </span>
              )}
              <Button
                variant="destructive"
                onClick={() => onDecline(investor)}
                disabled={investor.contactPending}
              >
                <X className="size-3.5" />
                Decline
              </Button>
              <Button
                className={approveClass}
                onClick={() => onApprove(investor)}
                disabled={investor.contactPending}
              >
                <Check className="size-3.5" strokeWidth={2.5} />
                Approve
              </Button>
            </>
          )}
        </DialogFooter>
      )}
    </>
  )
}

function formatRelative(iso?: string): string {
  if (!iso) return ''
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ''
  const diffMs = Date.now() - date.getTime()
  const diffH = Math.floor(diffMs / (1000 * 60 * 60))
  if (diffH < 1) return 'just now'
  if (diffH < 24) return `${diffH}h ago`
  const diffD = Math.floor(diffH / 24)
  if (diffD < 7) return `${diffD}d ago`
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

// ─── Engaged table ───────────────────────────────────────────────────────────

/**
 * The concrete next step an investor's reply asked for. Rendered under the
 * reply summary so "email us the deck" comes with the address to email — the
 * summary alone left founders opening LinkedIn to find it again.
 */
function ReplyRedirect({ redirect }: { redirect: NonNullable<RosterMatch['redirect']> }) {
  const targets = [
    redirect.email && { label: 'Send to', href: `mailto:${redirect.email}`, text: redirect.email },
    redirect.url && { label: 'Apply at', href: redirect.url, text: redirect.url },
  ].filter((t): t is { label: string; href: string; text: string } => Boolean(t))
  if (targets.length === 0) return null
  return (
    <div className="mt-1.5 space-y-0.5 text-[12px]">
      {targets.map((t) => (
        <div key={t.href} className="flex items-center gap-1.5">
          <span className="font-medium text-amber-400">Action required</span>
          <span className="text-muted-foreground">· {t.label}</span>
          <a
            href={t.href}
            target="_blank"
            rel="noopener noreferrer"
            // The row itself opens the LinkedIn thread; the link must not do both.
            onClick={(e) => e.stopPropagation()}
            className="truncate text-foreground underline decoration-border underline-offset-2 hover:decoration-foreground"
          >
            {t.text}
          </a>
        </div>
      ))}
    </div>
  )
}

export function EngagedTable({
  entries,
  isPaused,
  onArchive,
  archiving,
  subject = 'Replies and meaningful interest',
  observe,
  focusId,
  onFocusRow,
}: {
  entries: RosterMatch[]
  isPaused: boolean
  onArchive?: (entry: RosterMatch) => void
  archiving?: Record<string, boolean>
  subject?: string
  /**
   * Supplies a ref callback per unread entry, so the Outcomes page can mark a
   * reply read once it has actually been on screen. Omitted elsewhere: the
   * campaign boards show these rows too, and scrolling past them there is not
   * the founder reading their replies.
   */
  observe?: (entry: RosterMatch) => RefCallback<HTMLElement> | undefined
  /**
   * The one row to scroll into view, and how. Set when the founder arrived from
   * a card that promised them unread replies — see the Outcomes page.
   */
  focusId?: string
  onFocusRow?: RefCallback<HTMLElement>
}) {
  return (
    <div className="overflow-hidden rounded-xl border border-emerald-500/30 bg-card/30">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border/40 px-5 py-3">
        <div className="flex items-center gap-2.5">
          <span className="inline-flex items-center gap-1 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wider text-emerald-400">
            Engaged · {entries.length}
          </span>
          <span className="text-[12px] text-muted-foreground">
            {isPaused ? 'Replies from before the pause' : subject}
          </span>
        </div>
      </div>
      <div className="border-b border-border/40 bg-emerald-500/5 px-5 py-3 text-[12.5px] leading-relaxed text-emerald-300/90">
        <strong className="font-semibold text-emerald-300">Take it from here.</strong> These
        contacts are ready for you to take the conversation over manually. Open the LinkedIn thread
        and reply directly. Once you've handled one, archive it to clear it off the board.
      </div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[620px] text-[13px]">
          <thead>
            <tr className="bg-card/40 text-[10.5px] uppercase tracking-wider text-muted-foreground/70">
              <th className="px-5 py-2.5 text-left font-medium">Investor</th>
              <th className="px-3 py-2.5 text-left font-medium">Contact</th>
              <th className="px-3 py-2.5 text-left font-medium">Last reply</th>
              <th className="px-3 py-2.5 text-left font-medium">When</th>
              <th className="px-5 py-2.5 text-right font-medium" />
            </tr>
          </thead>
          <tbody className="divide-y divide-border/30">
            {entries.map((entry) => (
              <EngagedRow
                // Message first: one fund can reply through two contacts, and
                // keying on the investor would collapse them into one row.
                key={entry.messageId ?? entry.leadId ?? entry.investorId}
                entry={entry}
                onArchive={onArchive ? () => onArchive(entry) : undefined}
                archiving={entry.leadId ? archiving?.[entry.leadId] : undefined}
                observe={entry.unread ? observe?.(entry) : undefined}
                focusRef={focusId && entry.messageId === focusId ? onFocusRow : undefined}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function EngagedRow({
  entry,
  onArchive,
  archiving,
  observe,
  focusRef,
}: {
  entry: RosterMatch
  onArchive?: () => void
  archiving?: boolean
  /** Ref callback that reports the row as read once it has been dwelled on. */
  observe?: RefCallback<HTMLElement>
  /** Set on the single row the page was asked to scroll to. */
  focusRef?: RefCallback<HTMLElement>
}) {
  const initials = makeInitials(entry.investorName)
  const hasCharges =
    entry.charges.views > 0 || entry.charges.returnVisits > 0 || entry.charges.calls > 0

  // The whole row is the door to the conversation — the board's instruction is
  // "open the LinkedIn thread and reply directly", and hunting for a link
  // inside the row made that a smaller target than it deserves.
  const openThread = () => {
    if (entry.linkedinUrl) window.open(entry.linkedinUrl, '_blank', 'noopener,noreferrer')
  }

  return (
    <tr
      // Both jobs on one node: report it read once it has been looked at, and
      // scroll to it when this is the row the founder was sent here for.
      ref={(node) => {
        observe?.(node)
        focusRef?.(node)
      }}
      {...(entry.linkedinUrl
        ? {
            role: 'link',
            tabIndex: 0,
            onClick: openThread,
            onKeyDown: (e: ReactKeyboardEvent<HTMLTableRowElement>) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                openThread()
              }
            },
          }
        : {})}
      className={cn(
        'transition-colors hover:bg-card/40',
        entry.linkedinUrl && 'cursor-pointer focus-visible:bg-card/40 focus-visible:outline-none'
      )}
    >
      <td className="px-5 py-3">
        <div className="flex items-center gap-2.5">
          {/* Unread marker, in the gutter so the rows stay aligned whether or
              not one is unread. */}
          {entry.unread ? (
            <span
              role="img"
              aria-label="Unread reply"
              className="size-1.5 rounded-full bg-primary"
            />
          ) : (
            <span className="size-1.5 shrink-0" />
          )}
          <div className="flex size-7 shrink-0 items-center justify-center rounded-md border border-border/40 bg-background/60 text-[11px] font-bold text-foreground/80">
            {initials}
          </div>
          <span className="font-medium text-foreground">{entry.investorName}</span>
        </div>
      </td>
      <td className="px-3 py-3 text-foreground/85">
        {entry.contactName ?? <span className="text-muted-foreground/60">—</span>}
        {entry.contactTitle && (
          <div className="text-[11.5px] text-muted-foreground/70">{entry.contactTitle}</div>
        )}
        {hasCharges && (
          <div className="mt-0.5 text-[11px] text-muted-foreground/70">
            {entry.charges.views > 0 &&
              `${entry.charges.views}× view${entry.charges.views > 1 ? 's' : ''} `}
            {entry.charges.returnVisits > 0 && `· ${entry.charges.returnVisits}× repeated `}
            {entry.charges.calls > 0 && '· call booked'}
          </div>
        )}
      </td>
      <td className="max-w-md px-3 py-3">
        {entry.lastReply ? (
          <span className="line-clamp-2 italic text-foreground/75">{entry.lastReply}</span>
        ) : (
          <span className="text-muted-foreground/60">—</span>
        )}
        {entry.redirect && <ReplyRedirect redirect={entry.redirect} />}
      </td>
      <td className="px-3 py-3 text-[12px] tabular-nums text-muted-foreground">
        {formatRelative(entry.lastEventAt)}
      </td>
      <td className="px-5 py-3 text-right">
        {onArchive && (
          <button
            type="button"
            // The row opens the thread; archiving must not do both.
            onClick={(e) => {
              e.stopPropagation()
              onArchive()
            }}
            disabled={archiving}
            title="Archive: you've taken this conversation over"
            className="inline-flex items-center gap-1 rounded-md border border-border/50 px-2 py-1 text-[11.5px] font-medium text-muted-foreground transition-colors hover:bg-card/60 hover:text-foreground disabled:opacity-50"
          >
            <Archive className="size-3.5" />
            Archive
          </button>
        )}
      </td>
    </tr>
  )
}

// ─── Kanban (Queued / In progress / Finished) ────────────────────────────────

type KanbanTone = 'blue' | 'emerald' | 'muted' | 'primary'

export interface KanbanColumnConfig {
  tone: KanbanTone
  title: string
  subtitle: string
  totalCount: number | string
  entries: RosterMatch[]
  /** Blurred placeholder cards rendered after the real entries (locked preview). */
  lockedCount?: number
  /** Footer rendered under the blurred cards (e.g. the unlock CTA). */
  lockedFooter?: ReactNode
  /** Plain footer rendered after the entries (e.g. "+ N more, best-fit first"). */
  footer?: ReactNode
  /** When set, cards are clickable (used by the Queued/approval column). */
  onCardClick?: (entry: RosterMatch) => void
  /** When set, each card shows inline approve/decline buttons (Queued column). */
  onApprove?: (entry: RosterMatch) => void
  onDecline?: (entry: RosterMatch) => void
  /** When set, the header shows an "Approve all" button (Queued column). */
  onApproveAll?: () => void
  /** Number of contacts still awaiting a decision — shown in the button's aria-label. */
  approveAllPending?: number
  /** True while a bulk approval is in flight. */
  approvingAll?: boolean
  /** When set, the header shows a "Skip the rest" button beside "Approve all". */
  onSkipAll?: () => void
  /** True while a bulk skip is in flight. */
  skippingAll?: boolean
  /** When set, each card shows an Archive button (Finished column). */
  onArchive?: (entry: RosterMatch) => void
  /** Per-lead approval state, keyed by leadId, driving the card's button/badge. */
  resolutions?: Record<string, ResolveState>
  /** Per-lead in-flight archive state, keyed by leadId. */
  archiving?: Record<string, boolean>
  /** DOM id used as a scroll target (e.g. the banner's "Review now" CTA). */
  anchorId?: string
  /** When true, multi-contact fund cards get a Recommended badge on the first entry. */
  showRecommended?: boolean
  /** When true, cards are revealed incrementally as the column scrolls into view. */
  incremental?: boolean
  /** When true, the per-card stage pill is hidden (the column header already says it). */
  hideStatePill?: boolean
}

// The outbound flow, as the three phases we surface, each backed by a real
// HeyReach webhook milestone: warming up (viewed profile / liked a post before
// the request goes out) → reached out (connection request sent) → connected
// (request accepted, awaiting their reply; their reply moves the contact to the
// Engaged table). The active node is the current phase. Engagement signals
// (profile views, return visits) ride along as the charges line below.
const STEPPER_STEPS = ['Warming up', 'Reached out', 'Connected'] as const

function SequenceStepper({ progress }: { progress: RosterProgress }) {
  // Each phase is gated on its real HeyReach milestone. "Reached out" requires
  // the actual connection-request-sent webhook — NOT our own push time, since a
  // lead can be pushed and warmed up (viewed/liked) well before the request goes
  // out. `reachedOut`/`warmedUp` stay monotonic with the later milestones.
  const connected = progress.acceptedAt != null
  const reachedOut = progress.connectionRequestSentAt != null || connected
  const warmedUp = progress.viewedProfileAt != null || progress.likedPostAt != null || reachedOut
  // A dot is solid once its milestone is reached: warming up completes when the
  // request is sent, reached-out completes on accept, and Connected fills solid
  // on accept too (it's a reached milestone, not a hollow "current" ring).
  const done = [reachedOut, connected, connected]
  const currentIdx = connected ? 2 : reachedOut ? 1 : 0
  const caption = connected
    ? 'Connected · awaiting reply'
    : reachedOut
      ? 'Reached out · awaiting acceptance'
      : warmedUp
        ? 'Warming up · viewed profile, liked a post'
        : 'Warming up · viewing profile, first message within 24h'

  return (
    <div className="mt-2.5">
      <div className="flex items-center">
        {STEPPER_STEPS.map((label, i) => (
          <Fragment key={label}>
            {i > 0 && (
              <span
                className={cn('h-px flex-1', i <= currentIdx ? 'bg-blue-500/50' : 'bg-border/50')}
              />
            )}
            <span
              className={cn(
                'size-2.5 shrink-0 rounded-full border',
                done[i]
                  ? 'border-blue-500 bg-blue-500'
                  : i === currentIdx
                    ? 'border-blue-500 bg-blue-500/25'
                    : 'border-border/60 bg-transparent'
              )}
            />
          </Fragment>
        ))}
      </div>
      <div className="mt-1 flex justify-between text-[9.5px] font-medium uppercase tracking-wide text-muted-foreground/70">
        {STEPPER_STEPS.map((label, i) => (
          <span
            key={label}
            className={cn(done[i] || i === currentIdx ? 'text-foreground/70' : undefined)}
          >
            {label}
          </span>
        ))}
      </div>
      <p className="mt-1.5 text-[11px] text-muted-foreground/85">{caption}</p>
    </div>
  )
}

export function RosterKanban({ columns }: { columns: KanbanColumnConfig[] }) {
  return (
    <div
      className={cn(
        'grid grid-cols-1 gap-4',
        columns.length === 4 ? 'md:grid-cols-4' : 'md:grid-cols-3'
      )}
    >
      {columns.map((col) => (
        <KanbanColumn key={col.title} {...col} />
      ))}
    </div>
  )
}

function KanbanColumn({
  tone,
  title,
  subtitle,
  totalCount,
  entries,
  onCardClick,
  onApprove,
  onDecline,
  onApproveAll,
  approveAllPending,
  approvingAll,
  onSkipAll,
  skippingAll,
  onArchive,
  resolutions,
  archiving,
  anchorId,
  showRecommended,
  incremental,
  lockedCount,
  lockedFooter,
  footer,
  hideStatePill,
}: KanbanColumnConfig) {
  // Reveal cards in pages as the column scrolls into view — keeps a queued
  // column with hundreds of contacts from rendering every card up front.
  const { visible, hasMore, sentinelRef } = useIncrementalReveal(entries)
  const renderedEntries = incremental ? visible : entries
  const headerToneClass =
    tone === 'blue'
      ? 'text-blue-400 bg-blue-500/10 border-blue-500/30'
      : tone === 'emerald'
        ? 'text-emerald-400 bg-emerald-500/10 border-emerald-500/30'
        : tone === 'primary'
          ? 'text-primary bg-primary/10 border-primary/30'
          : 'text-muted-foreground bg-card border-border/60'

  return (
    <div
      id={anchorId}
      className={cn(
        'flex flex-col rounded-xl border bg-card/20 scroll-mt-24',
        tone === 'primary'
          ? 'border-primary/30'
          : tone === 'emerald'
            ? 'border-emerald-500/30'
            : 'border-border/40'
      )}
    >
      <div className="border-b border-border/40 px-4 py-3">
        <div className="flex min-h-7 flex-wrap items-center gap-2">
          <span
            className={cn(
              'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10.5px] font-semibold uppercase tracking-wider',
              headerToneClass
            )}
          >
            {title} · {totalCount}
          </span>
          {onApproveAll && (
            <button
              type="button"
              disabled={approvingAll}
              onClick={onApproveAll}
              aria-label={`Approve all${(approveAllPending ?? 0) > 0 ? ` ${approveAllPending}` : ''} queued contacts`}
              className="ml-auto inline-flex items-center gap-1.5 rounded-md border border-emerald-500/30 bg-emerald-500/10 px-2.5 py-1 text-[11px] font-semibold text-emerald-400 transition-colors hover:bg-emerald-500/20 disabled:opacity-50"
            >
              {approvingAll ? (
                <Loader2 className="size-3.5 animate-spin" />
              ) : (
                <Check className="size-3.5" strokeWidth={2.5} />
              )}
              Approve all
            </button>
          )}
          {onSkipAll && (
            <button
              type="button"
              disabled={skippingAll}
              onClick={onSkipAll}
              aria-label={`Skip the remaining${(approveAllPending ?? 0) > 0 ? ` ${approveAllPending}` : ''} queued contacts`}
              title="Skipping is not rejecting. These move to your Skipped list and you can bring them back anytime."
              className={cn(
                'inline-flex items-center gap-1.5 rounded-md border border-border/60 px-2.5 py-1 text-[11px] font-semibold text-muted-foreground transition-colors hover:text-foreground disabled:opacity-50',
                !onApproveAll && 'ml-auto'
              )}
            >
              {skippingAll ? (
                <Loader2 className="size-3.5 animate-spin" />
              ) : (
                <SkipForward className="size-3.5" strokeWidth={2.5} />
              )}
              Skip the rest
            </button>
          )}
        </div>
        <p className="mt-1.5 line-clamp-2 min-h-[2.8em] text-[11.5px] leading-[1.4] text-muted-foreground">
          {subtitle}
        </p>
      </div>

      <div className="flex flex-col gap-2 p-3">
        {entries.length === 0 ? (
          <div className="rounded-lg border border-dashed border-border/40 px-3 py-4 text-center text-[11.5px] text-muted-foreground/60">
            No one here yet.
          </div>
        ) : (
          (() => {
            const seenFunds = new Set<string>()
            return renderedEntries.map((entry) => {
              const fund = entry.investorName
              const hasSiblings =
                showRecommended && entries.filter((e) => e.investorName === fund).length > 1
              const isRecommended = Boolean(hasSiblings && !seenFunds.has(fund))
              seenFunds.add(fund)
              return (
                <KanbanCard
                  key={entry.leadId ?? entry.investorId}
                  entry={entry}
                  tone={tone}
                  onClick={onCardClick ? () => onCardClick(entry) : undefined}
                  onApprove={onApprove && entry.leadId ? () => onApprove(entry) : undefined}
                  onDecline={onDecline && entry.leadId ? () => onDecline(entry) : undefined}
                  onArchive={onArchive && entry.leadId ? () => onArchive(entry) : undefined}
                  archiving={entry.leadId ? archiving?.[entry.leadId] : undefined}
                  resolution={
                    (entry.leadId ? resolutions?.[entry.leadId] : undefined) ?? entry.approvalState
                  }
                  isRecommended={isRecommended}
                  hideStatePill={hideStatePill}
                />
              )
            })
          })()
        )}

        {incremental && hasMore && (
          <div ref={sentinelRef} className="flex justify-center py-3" aria-hidden="true">
            <Loader2 className="size-4 animate-spin text-muted-foreground/50" />
          </div>
        )}

        {(lockedCount ?? 0) > 0 && (
          <>
            <div className="pointer-events-none select-none space-y-2 blur-sm" aria-hidden="true">
              {['a', 'b', 'c'].slice(0, Math.min(lockedCount ?? 0, 3)).map((k) => (
                <LockedCardSkeleton key={k} />
              ))}
            </div>
            {lockedFooter}
          </>
        )}

        {footer}
      </div>
    </div>
  )
}

// Blurred placeholder card for the locked-preview tail of a column. Mirrors the
// KanbanCard silhouette (avatar + two text lines + a pill) so the blur reads as
// "more real cards here", not empty boxes.
function LockedCardSkeleton() {
  return (
    <div className="rounded-lg border border-border/40 bg-background/60 px-3 py-2.5">
      <div className="flex items-start gap-2.5">
        <div className="size-7 shrink-0 rounded-md border border-border/40 bg-muted-foreground/20" />
        <div className="min-w-0 flex-1">
          <div className="h-3.5 w-28 rounded bg-muted-foreground/30" />
          <div className="mt-1.5 h-3 w-40 rounded bg-muted-foreground/20" />
        </div>
      </div>
      <div className="mt-2.5 h-4 w-24 rounded bg-muted-foreground/15" />
    </div>
  )
}

function KanbanCard({
  entry,
  tone,
  onClick,
  onApprove,
  onDecline,
  onArchive,
  archiving,
  resolution,
  isRecommended,
  hideStatePill,
}: {
  entry: RosterMatch
  tone: KanbanTone
  onClick?: () => void
  onApprove?: () => void
  onDecline?: () => void
  onArchive?: () => void
  archiving?: boolean
  resolution?: ResolveState
  isRecommended?: boolean
  hideStatePill?: boolean
}) {
  const stagePillClass =
    tone === 'blue'
      ? 'border-blue-500/30 bg-blue-500/10 text-blue-400'
      : tone === 'emerald'
        ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-400'
        : tone === 'primary'
          ? 'border-primary/30 bg-primary/10 text-primary'
          : 'border-border/60 bg-card text-muted-foreground'
  const initials = makeInitials(entry.investorName)
  const hasCharges =
    entry.charges.views > 0 || entry.charges.returnVisits > 0 || entry.charges.calls > 0
  const isApproved = resolution === 'approved'
  const isDeclined = resolution === 'declined'
  const busy = resolution === 'approving' || resolution === 'declining'
  const stateLabel = isApproved ? 'Approved' : isDeclined ? 'Declined' : null
  // Action buttons only when the caller wired handlers (the live board). A
  // resolution without handlers (pre-launch "Approved" after the review step)
  // renders as a plain state label — no buttons that can't act yet.
  const hasResolveActions = Boolean(onApprove || onDecline)
  const showResolve = hasResolveActions || resolution != null

  return (
    <article
      className={cn(
        'rounded-lg border border-border/40 bg-background/60 px-3 py-2.5 transition-colors hover:border-border/80 hover:bg-background/80',
        onClick && 'cursor-pointer'
      )}
      {...(onClick
        ? {
            role: 'button',
            tabIndex: 0,
            onClick,
            onKeyDown: (event: ReactKeyboardEvent) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault()
                onClick()
              }
            },
          }
        : {})}
    >
      <div className="flex items-start gap-2.5">
        <div className="flex size-7 shrink-0 items-center justify-center rounded-md border border-border/40 bg-background/80 text-[10.5px] font-bold text-foreground/80">
          {initials}
        </div>
        <div className="min-w-0 flex-1">
          {entry.contactName ? (
            <>
              <div className="truncate text-[13px] font-semibold text-foreground">
                {entry.contactName}
              </div>
              <div className="truncate text-[11.5px] text-muted-foreground">
                {entry.companyName
                  ? `${entry.contactTitle ?? entry.companyName}${entry.contactTitle ? ` · ${entry.companyName}` : ''}`
                  : entry.profile && entry.contactTitle
                    ? `${entry.contactTitle} · ${entry.investorName}`
                    : entry.investorName}
              </div>
              {entry.fundName && (
                <div className="mt-0.5 inline-flex items-center rounded-full border border-primary/25 bg-primary/8 px-1.5 py-0.5 text-[10px] font-semibold text-primary/70">
                  target {entry.fundName}
                </div>
              )}
            </>
          ) : (
            <>
              <div className="truncate text-[13px] font-semibold text-foreground">
                {entry.investorName}
              </div>
              {entry.contactPending && (
                <div className="mt-0.5 flex items-center gap-1 text-[11.5px] text-muted-foreground">
                  <Loader2 className="size-3 shrink-0 animate-spin" />
                  <span className="truncate">Finding the best partner to reach…</span>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {isRecommended && (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="mt-1.5 inline-flex cursor-default items-center gap-1 rounded-md border border-amber-500/20 bg-amber-500/5 px-1.5 py-0.5 text-[10.5px] font-medium text-amber-400/70">
                <Star className="size-2.5 fill-amber-400/70" />
                Best Match
              </div>
            </TooltipTrigger>
            <TooltipContent
              side="bottom"
              className="max-w-[220px] bg-foreground text-center text-[11.5px] text-background"
            >
              Top-ranked contact from this fund. We'll reach out to them first before contacting
              others from the same firm.
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}

      {entry.kind && (
        <div className="mt-1.5">
          <span
            className={cn(
              'inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider',
              entry.kind === 'investor'
                ? 'border-orange-500/30 bg-orange-500/10 text-orange-400'
                : 'border-purple-500/30 bg-purple-500/10 text-purple-400'
            )}
          >
            {entry.kind === 'investor' ? 'Investor' : 'Warm intro'}
          </span>
        </div>
      )}

      {entry.contactScope && (
        <p className="mt-1.5 line-clamp-2 text-[11px] leading-snug text-muted-foreground/70">
          {entry.contactScope}
        </p>
      )}

      {entry.progress ? (
        <SequenceStepper progress={entry.progress} />
      ) : entry.profile ? (
        <div className="mt-2 flex flex-wrap gap-1">
          {[...entry.profile.stages.slice(0, 1), ...entry.profile.sectors.slice(0, 1)].map(
            (tag) => (
              <MiniTag key={tag}>{tag}</MiniTag>
            )
          )}
        </div>
      ) : showResolve || hideStatePill ? null : (
        // Resolve cards carry their state as plain text in the action row below
        // (not a pill), so only non-resolve cards show the stage pill here.
        <div className="mt-2.5">
          <span
            className={cn(
              'inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-[10.5px] font-medium',
              stagePillClass
            )}
          >
            {entry.stage}
          </span>
        </div>
      )}

      {hasCharges && (
        <div className="mt-2 text-[11px] text-muted-foreground/85">
          {entry.charges.views > 0 &&
            `${entry.charges.views}× view${entry.charges.views > 1 ? 's' : ''} `}
          {entry.charges.returnVisits > 0 && `· ${entry.charges.returnVisits}× repeated `}
          {entry.charges.calls > 0 &&
            `· ${entry.charges.calls}× call${entry.charges.calls > 1 ? 's' : ''}`}
        </div>
      )}

      {entry.lastEventAt && (
        <div className="mt-2 border-t border-border/30 pt-1.5 text-[10.5px] text-muted-foreground/60">
          {formatRelative(entry.lastEventAt)}
        </div>
      )}

      {showResolve && (
        <div className="mt-2.5 flex items-center gap-2 border-t border-border/30 pt-2">
          {stateLabel ? (
            <span
              className={cn(
                'text-[11px] font-semibold',
                isApproved ? 'text-emerald-400' : 'text-red-400'
              )}
            >
              {stateLabel}
            </span>
          ) : (
            <span className="text-[11px] font-medium text-muted-foreground">Awaiting approval</span>
          )}
          {hasResolveActions && (
            <div className="ml-auto flex items-center gap-1.5">
              <CardActionButton
                variant="approve"
                active={isApproved}
                loading={resolution === 'approving'}
                disabled={busy}
                onClick={onApprove}
              />
              <CardActionButton
                variant="decline"
                active={isDeclined}
                loading={resolution === 'declining'}
                disabled={busy}
                onClick={onDecline}
              />
            </div>
          )}
        </div>
      )}

      {onArchive && (
        <div className="mt-2.5 flex items-center justify-between gap-2 border-t border-border/30 pt-2">
          {entry.finishedReason ? (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="inline-flex cursor-default items-center gap-1 text-[11px] text-muted-foreground">
                    <Info className="size-3 shrink-0" />
                    {FINISHED_REASON_COPY[entry.finishedReason].label}
                  </span>
                </TooltipTrigger>
                <TooltipContent
                  side="bottom"
                  className="max-w-[220px] bg-foreground text-center text-[11.5px] text-background"
                >
                  {FINISHED_REASON_COPY[entry.finishedReason].tooltip}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ) : (
            <span />
          )}
          <button
            type="button"
            disabled={archiving}
            onClick={(event) => {
              event.stopPropagation()
              onArchive()
            }}
            className="inline-flex items-center gap-1.5 rounded-md border border-border/60 bg-card px-2.5 py-1 text-[11px] font-medium text-muted-foreground transition-colors hover:bg-card/70 disabled:opacity-50"
          >
            {archiving ? (
              <Loader2 className="size-3.5 animate-spin" />
            ) : (
              <Archive className="size-3.5" />
            )}
            Archive
          </button>
        </div>
      )}
    </article>
  )
}

function CardActionButton({
  variant,
  active = false,
  loading = false,
  disabled = false,
  onClick,
}: {
  variant: 'approve' | 'decline'
  /** This is the current decision — rendered solid and not clickable. */
  active?: boolean
  loading?: boolean
  /** Locked out because the other action is in flight. */
  disabled?: boolean
  onClick?: () => void
}) {
  const isApprove = variant === 'approve'
  const tone = isApprove
    ? active
      ? 'border-emerald-600 bg-emerald-600 text-white'
      : 'border-emerald-500/30 bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20'
    : active
      ? 'border-red-600 bg-red-600 text-white'
      : 'border-red-500/30 bg-red-500/10 text-red-400 hover:bg-red-500/20'
  return (
    <button
      type="button"
      aria-label={isApprove ? 'Approve' : 'Decline'}
      aria-pressed={active}
      disabled={disabled || loading || active}
      onClick={(event) => {
        event.stopPropagation()
        onClick?.()
      }}
      className={cn(
        'flex size-7 items-center justify-center rounded-md border transition-colors',
        tone,
        // Dim only when locked out by an in-flight action — the active choice
        // stays solid so it reads as the selected state.
        (disabled || loading) && !active && 'opacity-50'
      )}
    >
      {loading ? (
        <Loader2 className="size-3.5 animate-spin" />
      ) : isApprove ? (
        <Check className="size-3.5" />
      ) : (
        <X className="size-3.5" />
      )}
    </button>
  )
}

export function FitChip({ score, size = 'sm' }: { score: number; size?: 'sm' | 'lg' }) {
  const strong = score >= 70
  const tone = strong
    ? 'bg-emerald-500/15 text-emerald-400'
    : 'border border-border/60 bg-card text-muted-foreground'
  const chip =
    size === 'lg' ? (
      <div
        className={cn(
          'flex shrink-0 cursor-default items-center justify-center rounded-xl px-3.5 py-2',
          tone
        )}
      >
        <span className="text-[22px] font-bold leading-none tabular-nums">{score}</span>
      </div>
    ) : (
      <span
        className={cn(
          'inline-flex shrink-0 cursor-default items-center rounded-full px-1.5 py-0.5 text-[10.5px] font-bold tabular-nums',
          tone
        )}
      >
        {score}
      </span>
    )
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>{chip}</TooltipTrigger>
        <TooltipContent
          side="bottom"
          className="max-w-[220px] bg-foreground text-center text-[11.5px] text-background"
        >
          Matching score: how well this fund fits your startup.
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

function MiniTag({ children }: { children: ReactNode }) {
  return (
    <span className="inline-flex items-center rounded-md border border-border/50 bg-card/60 px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
      {children}
    </span>
  )
}

function ProfileTags({ label, tags }: { label: string; tags: string[] }) {
  if (tags.length === 0) return null
  return (
    <div>
      <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/60">
        {label}
      </div>
      <div className="flex flex-wrap gap-1">
        {tags.map((tag) => (
          <MiniTag key={tag}>{tag}</MiniTag>
        ))}
      </div>
    </div>
  )
}

function makeInitials(name: string): string {
  return (
    name
      .split(/\s+/)
      .map((w) => w[0])
      .join('')
      .slice(0, 2)
      .toUpperCase() || '?'
  )
}

// ════════════════════════════════════════════════════════════════════════════
// Utilities
// ════════════════════════════════════════════════════════════════════════════

function formatUsd(cents: number): string {
  return (cents / 100).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })
}

/**
 * Maps a portfolio queue row into the shared roster shape, so the portfolio board
 * and the replies page render through the same cards and tables as the investor side.
 */
export const portfolioItemToRoster = (
  item: {
    id: string
    contactId: string
    name: string | null
    title: string | null
    linkedinUrl: string
    companyName: string
    companyUrl: string | null
    fundName: string
    firstReplyAt: string | null
    replySummary: string | null
    /** Null while the founder still has this reply to read. */
    seenAt?: string | null
    warmIntroCandidate: boolean
    warmIntroConfirmedAt: string | null
    approvalState: 'awaiting' | 'approved' | null
    pushedAt: string | null
    connectionRequestSentAt: string | null
    connectionAcceptedAt: string | null
    msg1: string | null
    msg2: string | null
    msg3: string | null
  },
  group: RosterGroupKey
): RosterMatch => ({
  investorId: item.fundName,
  leadId: item.id,
  contactId: item.contactId,
  investorName: item.fundName,
  contactName: item.name,
  contactTitle: item.title,
  contactScope: null,
  group,
  stage:
    group === 'queued'
      ? 'Queued'
      : group === 'in_progress'
        ? item.connectionAcceptedAt
          ? 'Connected & awaiting reply'
          : item.connectionRequestSentAt
            ? 'Reached out'
            : 'Warming up'
        : group === 'engaged'
          ? item.warmIntroConfirmedAt
            ? 'Warm intro confirmed'
            : item.warmIntroCandidate
              ? 'Warm intro'
              : 'Replied'
          : 'Finished',
  // Portfolio sequences end with no warm intro → mirror the investor "exhausted"
  // finished reason so the Finished card shows the same explanation + archive.
  finishedReason: group === 'finished' ? 'exhausted' : null,
  linkedinUrl: item.linkedinUrl,
  charges: { views: 0, returnVisits: 0, calls: 0 },
  companyName: item.companyName,
  companyUrl: item.companyUrl,
  fundName: item.fundName,
  previewMessages: { msg1: item.msg1, msg2: item.msg2, msg3: item.msg3 },
  lastReply: item.replySummary ?? undefined,
  messageId: item.id,
  unread: item.firstReplyAt != null && item.seenAt == null,
  lastEventAt: item.firstReplyAt ?? undefined,
  approvalState: item.approvalState === 'approved' ? 'approved' : undefined,
  progress:
    group === 'in_progress'
      ? {
          viewedProfileAt: null,
          likedPostAt: null,
          connectionRequestSentAt: item.connectionRequestSentAt,
          sentAt: item.pushedAt,
          acceptedAt: item.connectionAcceptedAt,
          views: 0,
          returnVisits: 0,
        }
      : undefined,
})
